import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Seg } from '@/components/ui/seg';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { useLeague } from '@/contexts/LeagueContext';
import { useToast } from '@/hooks/use-toast';
import { useAddDrop, getTodayET, type RosterPlayer } from '@/hooks/useAddDrop';
import { trackEvent } from '@/lib/analytics';
import AddDropDialog, { type WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import { useWaiverClaims } from '@/hooks/useWaiverClaims';
import MLBBetaTrades from '@/components/mlb-beta/MLBBetaTrades';
import MatchupBoxScore, { type BoxScorePlayer } from '@/components/mlb-beta/MatchupBoxScore';
import MLBPlayerCardModal from '@/components/mlb-beta/MLBPlayerCardModal';
import { usePlayerMatchScores } from '@/hooks/usePlayerMatchScores';
import { cn } from '@/lib/utils';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import StatsColumns, { type StatsRow } from '@/components/mlb-beta/StatsColumns';
import { type SeasonStats, type StatColumn, BATTER_COLUMNS, PITCHER_COLUMNS, ALL_COLUMNS, PITCHER_POSITIONS, getSabPtsDisplay } from '@/components/mlb-beta/statsTableDefs';

import {
  ArrowUpDown,
  ArrowLeftRight,
  UserPlus,
  UserMinus,
  Lock,
  Search,
  Users,
  Pencil,
  Check,
  X,
  HeartPulse,
  Activity,
  AlertTriangle,
  Zap,
  ChevronLeft,
  ChevronRight,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import type { FreeAgentPlayer } from '@/hooks/useAddDrop';

const DEFAULT_STARTER_SLOTS = ['C', '1B', '2B', 'SS', '3B', 'OF1', 'OF2', 'OF3', 'DH', 'UTIL', 'SP1', 'SP2', 'RP1', 'RP2'];
const normalizeSlot = (slot: string) => (slot || 'BENCH').toUpperCase().trim();

/** Build starter slots dynamically from category_config roster_mins + pitcher_slot_type */
function buildStarterSlots(config: any): string[] {
  if (!config?.roster_mins) return DEFAULT_STARTER_SLOTS;
  const mins = config.roster_mins as Record<string, number>;
  const pitcherType = config.pitcher_slot_type || 'split';
  const slots: string[] = [];

  // Non-pitcher, non-bench positions — numbered slots for multi-slot positions
  const posOrder = ['C', '1B', '2B', 'SS', '3B', 'OF', 'DH', 'UTIL'];
  for (const pos of posOrder) {
    const count = mins[pos] ?? 0;
    if (count <= 0) continue;
    if (count === 1) {
      slots.push(pos);
    } else {
      for (let i = 1; i <= count; i++) slots.push(`${pos}${i}`);
    }
  }

  // Pitcher slots
  if (pitcherType === 'combined') {
    const pCount = mins['P'] ?? 0;
    if (pCount === 1) {
      slots.push('P');
    } else {
      for (let i = 1; i <= pCount; i++) slots.push(`P${i}`);
    }
  } else {
    const spCount = mins['SP'] ?? 0;
    const rpCount = mins['RP'] ?? 0;
    if (spCount === 1) { slots.push('SP'); } else { for (let i = 1; i <= spCount; i++) slots.push(`SP${i}`); }
    if (rpCount === 1) { slots.push('RP'); } else { for (let i = 1; i <= rpCount; i++) slots.push(`RP${i}`); }
  }

  return slots.length > 0 ? slots : DEFAULT_STARTER_SLOTS;
}

/** Build position eligibility that includes combined P slots */
function buildPositionEligibility(config: any): Record<string, string[]> {
  const pitcherType = config?.pitcher_slot_type || 'split';
  const mins = config?.roster_mins as Record<string, number> | undefined;

  // Build OF slots
  const ofCount = mins?.['OF'] ?? 3;
  const ofSlots = ofCount === 1 ? ['OF'] : Array.from({ length: ofCount }, (_, i) => `OF${i + 1}`);

  const base: Record<string, string[]> = {
    'C':   ['C', 'DH', 'UTIL', 'BENCH'],
    '1B':  ['1B', 'DH', 'UTIL', 'BENCH'],
    '2B':  ['2B', 'DH', 'UTIL', 'BENCH'],
    '3B':  ['3B', 'DH', 'UTIL', 'BENCH'],
    'SS':  ['SS', 'DH', 'UTIL', 'BENCH'],
    'OF':  [...ofSlots, 'DH', 'UTIL', 'BENCH'],
    'LF':  [...ofSlots, 'DH', 'UTIL', 'BENCH'],
    'CF':  [...ofSlots, 'DH', 'UTIL', 'BENCH'],
    'RF':  [...ofSlots, 'DH', 'UTIL', 'BENCH'],
    'DH':  ['DH', 'UTIL', 'BENCH'],
  };

  if (pitcherType === 'combined') {
    const pCount = mins?.['P'] ?? 5;
    const pSlots = pCount === 1 ? ['P'] : Array.from({ length: pCount }, (_, i) => `P${i + 1}`);
    base['SP'] = [...pSlots, 'BENCH'];
    base['RP'] = [...pSlots, 'BENCH'];
    base['P'] = [...pSlots, 'BENCH'];
    base['TWP'] = [...pSlots, 'BENCH'];
  } else {
    const spCount = mins?.['SP'] ?? 2;
    const rpCount = mins?.['RP'] ?? 2;
    const spSlots = spCount === 1 ? ['SP'] : Array.from({ length: spCount }, (_, i) => `SP${i + 1}`);
    const rpSlots = rpCount === 1 ? ['RP'] : Array.from({ length: rpCount }, (_, i) => `RP${i + 1}`);
    base['SP'] = [...spSlots, 'BENCH'];
    base['RP'] = [...rpSlots, 'BENCH'];
    base['P'] = [...spSlots, ...rpSlots, 'BENCH'];
    base['TWP'] = [...spSlots, ...rpSlots, 'BENCH'];
  }

  return base;
}

// Position eligibility: which slots each position can fill
const HITTER_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'OF', 'LF', 'CF', 'RF', 'DH'];

// Slot prefixes that belong to pitchers vs hitters for roster splitting
const PITCHER_SLOT_PREFIXES = ['SP', 'RP', 'P'];
const isPitcherSlot = (slot: string) => PITCHER_SLOT_PREFIXES.some(p => slot.replace(/\d+$/, '') === p);
const isPitcherPlayer = (p: { position_abbreviation?: string }) =>
  PITCHER_POSITIONS.includes(p.position_abbreviation || '');

/** Call daily-lineup edge function — passes session JWT directly via headers */
async function invokeDailyLineup(payload: Record<string, any>): Promise<{ body: any; error: string | null }> {
  try {
    // Read session from cache — do NOT call refreshSession() here because it
    // can clear auth state during page-load init and cause an auth regression.
    const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
    const sess = sessionData?.session;
    const accessToken = sess?.access_token;

    if (!accessToken) {
      return { body: null, error: 'No session. Please log in again.' };
    }

    // Pass the JWT directly so invoke() never falls back to the publishable key
    const { data, error, response } = await (supabase.functions.invoke as any)('daily-lineup', {
      body: payload,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) {
      let detail = error.message || 'Unknown error';
      try {
        const res = error.context || response;
        if (res && typeof res.json === 'function') {
          const body = await res.json();
          detail = body?.error || body?.message || JSON.stringify(body);
        }
      } catch { /* body consumed */ }
      console.error('[invokeDailyLineup] error:', detail);
      return { body: null, error: detail };
    }
    if (data?.error) return { body: data, error: data.error };
    return { body: data, error: null };
  } catch (e: any) {
    return { body: null, error: e?.message || 'Network error' };
  }
}

interface MLBBetaMyTeamProps {
  onNavigateToTeam?: (teamId: string) => void;
}

const MLBBetaMyTeam = ({ onNavigateToTeam }: MLBBetaMyTeamProps) => {
  const { activeLeague, teams, renameMyTeam } = useLeague();
  const { toast } = useToast();

  // Declared before useAddDrop because the per-game locks are computed FOR this
  // date. Reading them against today while the user edits another day is the
  // defect this fixes: once today's games were final, every player whose team had
  // played was reported locked for tomorrow's lineup.
  const today = useMemo(() => getTodayET(), []);
  const [viewDate, setViewDate] = useState(today);

  const addDrop = useAddDrop(viewDate);
  const waiverClaims = useWaiverClaims();
  const waiverMode = (activeLeague?.waiver_type as WaiverMode) || 'fcfs';

  // Dynamic slots from league config
  const leagueConfig = activeLeague?.category_config as any;
  const STARTER_SLOTS = useMemo(() => buildStarterSlots(leagueConfig), [leagueConfig]);
  const dynamicEligibility = useMemo(() => buildPositionEligibility(leagueConfig), [leagueConfig]);

  // Dynamic eligibility functions that use league config instead of static POSITION_ELIGIBILITY
  const getEligibleSlotsD = useCallback((position: string): string[] => {
    return dynamicEligibility[position] || ['BENCH'];
  }, [dynamicEligibility]);

  const isEligibleForSlotD = useCallback((position: string, slot: string, eligibility?: string[] | Record<string, number> | null): boolean => {
    if (slot === 'IL') return true;
    if (getEligibleSlotsD(position).includes(slot)) return true;
    const elig = Array.isArray(eligibility) ? eligibility : eligibility ? Object.keys(eligibility) : null;
    if (elig) {
      for (const pos of elig) {
        if (getEligibleSlotsD(pos).includes(slot)) return true;
      }
    }
    return false;
  }, [getEligibleSlotsD]);

  useEffect(() => { trackEvent('roster_viewed'); }, []);

  // Daily lineup management (per_game mode only)
  const isDailyMode = leagueConfig?.lineup_lock_type === 'per_game';
  const isViewingToday = viewDate === today;
  const isViewingPast = viewDate < today;
  const [dailyLocks, setDailyLocks] = useState<Map<string, string>>(new Map()); // player_id -> locked_at
  // Daily lineup overlay: when viewing a non-today date, stores player_id → { slot, is_starter }
  const [dailyOverlay, setDailyOverlay] = useState<Map<string, { slot: string; is_starter: boolean }> | null>(null);

  // Date navigation helpers
  const shiftDate = (days: number) => {
    const d = bareDateToDate(viewDate);
    d.setDate(d.getDate() + days);
    setViewDate(d.toISOString().slice(0, 10));
  };
  const formatViewDate = (dateStr: string) => {
    const d = bareDateToDate(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Game info per team abbreviation (for viewDate)
  type GameInfo = { opponent: string; isHome: boolean; status: string; startTime?: string };
  /**
   * Keyed by MLB TEAM ID, not abbreviation.
   *
   * Abbreviations are not unique across baseball. `Toros del Este` (Dominican
   * winter league, team 668) abbreviates to `TOR`, the same as the Toronto Blue
   * Jays -- so an abbreviation-keyed lookup rendered TORONTO's opponent, start
   * time and live status next to a player who was in the Dominican Republic.
   * Team ids are unique across every level, and game_schedule holds only the 30
   * MLB clubs, so a minor/winter-league id simply misses and the row shows the
   * muted "no game" dash that is the honest answer.
   *
   * Keyed on the id rather than special-casing TOR because the collision is a
   * CLASS, not an instance: a new winter-league or independent team could
   * collide next season and nothing would catch it.
   */
  const [gameInfoMap, setGameInfoMap] = useState<Map<number, GameInfo>>(new Map());
  const [probablePitcherMlbIds, setProbablePitcherMlbIds] = useState<Set<number>>(new Set());

  // Static MLB team ID → abbreviation map (IDs are stable)
  const TEAM_ID_ABBR: Record<number, string> = useMemo(() => ({
    108:'LAA',109:'AZ',110:'BAL',111:'BOS',112:'CHC',113:'CIN',114:'CLE',
    115:'COL',116:'DET',117:'HOU',118:'KC',119:'LAD',120:'WSH',121:'NYM',
    133:'ATH',134:'PIT',135:'SD',136:'SEA',137:'SF',138:'STL',139:'TB',
    140:'TEX',141:'TOR',142:'MIN',143:'PHI',144:'ATL',145:'CWS',146:'MIA',
    147:'NYY',158:'MIL',
  }), []);

  // Ensure daily_lineups + fetch locks + overlay for viewDate
  useEffect(() => {
    if (!isDailyMode || !addDrop.myTeam) return;
    const teamId = addDrop.myTeam.id;
    const fetchDailyState = async () => {
      // Ensure rows exist for this date (creates from roster_assignments if missing)
      await invokeDailyLineup({ action: 'ensure', team_id: teamId, game_date: viewDate });

      // Fetch full daily lineup for this date
      const { data: dailyRows } = await supabase
        .from('daily_lineups')
        .select('player_id, slot, is_starter, locked_at')
        .eq('team_id', teamId)
        .eq('game_date', viewDate);

      // Build locks map
      const locks = new Map<string, string>();
      (dailyRows || []).filter((r: any) => r.locked_at).forEach((r: any) => locks.set(r.player_id, r.locked_at));
      setDailyLocks(locks);

      // Build overlay map (slot/is_starter overrides for this date)
      if (dailyRows && dailyRows.length > 0) {
        const overlay = new Map<string, { slot: string; is_starter: boolean }>();
        dailyRows.forEach((r: any) => overlay.set(r.player_id, { slot: r.slot, is_starter: r.is_starter }));
        setDailyOverlay(overlay);
      } else {
        setDailyOverlay(null);
      }
    };
    fetchDailyState();
  }, [isDailyMode, addDrop.myTeam, viewDate]);

  // Fetch game schedule for viewDate to show opponent/status per player
  useEffect(() => {
    const fetchGames = async () => {
      const { data: games } = await supabase
        .from('game_schedule')
        .select('home_team_id, away_team_id, status, start_time, home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .eq('game_date', viewDate);
      if (!games || games.length === 0) {
        setGameInfoMap(new Map());
        setProbablePitcherMlbIds(new Set());
        return;
      }

      const info = new Map<number, GameInfo>();
      const ppIds = new Set<number>();
      for (const g of games) {
        const homeAbbr = TEAM_ID_ABBR[g.home_team_id];
        const awayAbbr = TEAM_ID_ABBR[g.away_team_id];
        const status = g.status || 'scheduled';
        // Key: the team id from game_schedule. Value still carries the OPPONENT's
        // abbreviation for display -- an opponent is always one of the 30 clubs.
        if (g.home_team_id != null) {
          info.set(g.home_team_id, { opponent: awayAbbr || '??', isHome: true, status, startTime: g.start_time });
        }
        if (g.away_team_id != null) {
          info.set(g.away_team_id, { opponent: homeAbbr || '??', isHome: false, status, startTime: g.start_time });
        }
        if (g.home_probable_pitcher_mlb_id) ppIds.add(g.home_probable_pitcher_mlb_id);
        if (g.away_probable_pitcher_mlb_id) ppIds.add(g.away_probable_pitcher_mlb_id);
      }
      setGameInfoMap(info);
      setProbablePitcherMlbIds(ppIds);
    };
    fetchGames();
  }, [viewDate, TEAM_ID_ABBR]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [swapSource, setSwapSource] = useState<RosterPlayer | null>(null);
  const [swapProcessing, setSwapProcessing] = useState(false);

  // Add/Drop dialog state
  const [addDropOpen, setAddDropOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState<RosterPlayer | null>(null);
  const [addTarget, setAddTarget] = useState<FreeAgentPlayer | null>(null);

  // Standalone drop confirmation dialog
  const [dropConfirmOpen, setDropConfirmOpen] = useState(false);
  const [dropConfirmTarget, setDropConfirmTarget] = useState<RosterPlayer | null>(null);

  // Free agent search state (for drop-first flow from My Team)
  const [showFASearch, setShowFASearch] = useState(false);
  const [faSearch, setFaSearch] = useState('');
  const [freeAgents, setFreeAgents] = useState<FreeAgentPlayer[]>([]);
  const [faLoading, setFaLoading] = useState(false);

  // Team name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [playerCardId, setPlayerCardId] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);


  // Raw season stats for stat table display (not scoring)
  const [seasonStatsMap, setSeasonStatsMap] = useState<Map<string, SeasonStats>>(new Map());
  const [myTeamStatView, setMyTeamStatView] = useState<'season' | 'pergame'>('season');

  // Time period filter: season (full), 7d/14d/30d (rolling), week (current matchup), today
  const [timePeriod, setTimePeriod] = useState<'season' | '7d' | '14d' | '30d' | 'today'>('season');
  const [periodStatsMap, setPeriodStatsMap] = useState<Map<string, SeasonStats>>(new Map());
  const [periodLoading, setPeriodLoading] = useState(false);

  // Weekly stats view
  const [weeklyView, setWeeklyView] = useState<'current' | number>('current');
  const [weeklyMatchup, setWeeklyMatchup] = useState<any>(null);
  const [weeklyPlayers, setWeeklyPlayers] = useState<BoxScorePlayer[]>([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState<{ week: number; status: string; week_start: string; week_end: string }[]>([]);
  const [maxWeek, setMaxWeek] = useState(1);

  // Current week info for score hooks
  const currentWeek = useMemo(() =>
    availableWeeks.find(w => w.week_start <= today && w.week_end >= today) || availableWeeks[availableWeeks.length - 1],
    [availableWeeks, today]
  );

  // ─── DROPPED PLAYERS: query roster_transactions for drops this week ───
  const [droppedPlayers, setDroppedPlayers] = useState<RosterPlayer[]>([]);
  useEffect(() => {
    if (!addDrop.myTeam?.id || !activeLeague?.id || !currentWeek) return;
    const fetchDropped = async () => {
      // Get drop transactions for this team during the current week
      const { data: txns } = await supabase
        .from('roster_transactions')
        .select('player_id, slot, executed_at')
        .eq('team_id', addDrop.myTeam!.id)
        .eq('league_id', activeLeague!.id)
        .in('transaction_type', ['drop', 'drop_only', 'trade_out', 'waiver_drop'])
        .gte('executed_at', currentWeek.week_start)
        .lte('executed_at', currentWeek.week_end + 'T23:59:59Z');
      if (!txns || txns.length === 0) { setDroppedPlayers([]); return; }

      // Filter out players still on roster (re-added after drop)
      const currentRosterIds = new Set(addDrop.roster.map(r => r.player_id));
      const droppedIds = [...new Set(txns.map(t => t.player_id))].filter(id => !currentRosterIds.has(id));
      if (droppedIds.length === 0) { setDroppedPlayers([]); return; }

      // Fetch player info
      const { data: players } = await supabase
        .from('players')
        .select('id, full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, mlb_team_id, mlb_id, headshot_url, status')
        .in('id', droppedIds);

      if (!players) { setDroppedPlayers([]); return; }
      const txnMap = new Map(txns.map(t => [t.player_id, t]));
      setDroppedPlayers(players.map(p => ({
        assignment_id: `dropped-${p.id}`,
        player_id: p.id,
        full_name: p.full_name,
        position_abbreviation: p.position_abbreviation,
        position_eligibility: p.position_eligibility,
        mlb_team_abbreviation: p.mlb_team_abbreviation,
        // Required by the id-keyed gameInfoMap lookup; omitting it silently
        // dropped this section's game info.
        mlb_team_id: p.mlb_team_id ?? null,
        mlb_id: p.mlb_id ?? null,
        headshot_url: p.headshot_url,
        slot: txnMap.get(p.id)?.slot || 'BENCH',
        is_starter: false,
        player_status: p.status || 'Active',
      })));
    };
    fetchDropped();
  }, [addDrop.myTeam?.id, activeLeague?.id, currentWeek, addDrop.roster]);

  // ─── SINGLE SOURCE OF TRUTH: read scores from player_match_scores ───
  // Include both current roster AND dropped players so their scores show
  const droppedPlayerIds = useMemo(() => droppedPlayers.map(p => p.player_id), [droppedPlayers]);
  const rosterPlayerIds = useMemo(() => {
    const ids = addDrop.roster.map(r => r.player_id);
    return [...ids, ...droppedPlayerIds];
  }, [addDrop.roster, droppedPlayerIds]);
  const seasonScores = usePlayerMatchScores({
    rosterPlayerIds,
    mode: 'season',
    enabled: !!addDrop.myTeam?.id,
  });
  const todayScores = usePlayerMatchScores({
    rosterPlayerIds,
    mode: 'today',
    enabled: !!addDrop.myTeam?.id && timePeriod === 'today',
  });

  // Scoring weights from league config (for season-level computation)
  const scoringWeights = useMemo(() => buildScoringWeights(leagueConfig), [leagueConfig]);

  // Active TP map based on time period
  // Season mode: compute from player_season_stats + scoring weights (complete, consistent)
  // 7d/14d/30d: compute from aggregated player_game_stats + scoring weights
  // Today: use player_match_scores (matchup-level, correct for the day's slice)
  const activeTpMap = useMemo(() => {
    if (timePeriod === 'season') {
      const m = new Map<string, number>();
      for (const [pid, stats] of seasonStatsMap) {
        m.set(pid, calculateTrashPoints(stats as Record<string, number>, scoringWeights));
      }
      return m;
    }
    if (timePeriod === '7d' || timePeriod === '14d' || timePeriod === '30d') {
      const m = new Map<string, number>();
      for (const [pid, stats] of periodStatsMap) {
        // Map 'lob' to 'left_on_base' for scoring compatibility
        const mapped = { ...(stats as any), left_on_base: (stats as any).lob || (stats as any).left_on_base || 0 };
        m.set(pid, calculateTrashPoints(mapped as Record<string, number>, scoringWeights));
      }
      return m;
    }
    const m = new Map<string, number>();
    for (const [pid, summary] of todayScores.playerScores) {
      m.set(pid, summary.total_points);
    }
    return m;
  }, [timePeriod, seasonStatsMap, scoringWeights, periodStatsMap, todayScores.playerScores]);

  // Fetch raw season stats for stat table display (no TP calculation)
  // Include dropped players so their stats row renders
  useEffect(() => {
    const rosterData = addDrop.roster;
    if (rosterData.length === 0 && droppedPlayers.length === 0) return;
    const playerIds = [...rosterData.map(r => r.player_id), ...droppedPlayers.map(p => p.player_id)];
    const fetchStats = async () => {
      const { data: seasonData } = await supabase
        .from('player_season_stats')
        .select('*')
        .in('player_id', playerIds)
        .eq('season', 2026);
      const ssMap = new Map<string, SeasonStats>();
      for (const row of (seasonData || [])) {
        ssMap.set(row.player_id, row as SeasonStats);
      }
      setSeasonStatsMap(ssMap);
    };
    fetchStats();
  }, [addDrop.roster, droppedPlayers]);

  // Fetch available weeks for the user's team
  useEffect(() => {
    if (!addDrop.myTeam || !activeLeague) return;
    const fetchWeeks = async () => {
      const { data } = await supabase
        .from('matchups')
        .select('week, status, week_start, week_end')
        .eq('league_id', activeLeague.id)
        .or(`home_team_id.eq.${addDrop.myTeam!.id},away_team_id.eq.${addDrop.myTeam!.id}`)
        .order('week');
      if (data) {
        setAvailableWeeks(data);
        setMaxWeek(Math.max(...data.map(d => d.week), 1));
      }
    };
    fetchWeeks();
  }, [addDrop.myTeam, activeLeague]);

  // Period-specific raw stats for stat table columns (not scoring)
  useEffect(() => {
    if (timePeriod === 'season') return;
    const rosterData = addDrop.roster;
    if (rosterData.length === 0) return;

    let dateStart: string;
    let dateEnd: string;

    if (timePeriod === 'today') {
      dateStart = today;
      dateEnd = today;
    } else {
      const days = timePeriod === '7d' ? 7 : timePeriod === '14d' ? 14 : 30;
      dateEnd = today;
      const d = bareDateToDate(today);
      d.setDate(d.getDate() - days);
      dateStart = d.toISOString().slice(0, 10);
    }

    const playerIds = rosterData.map(r => r.player_id);
    const ALL_STAT_KEYS = ['strikeouts', 'errors', 'gidp', 'caught_stealing', 'earned_runs', 'walks_allowed', 'wild_pitches', 'blown_saves', 'hit_batsmen', 'home_runs_allowed', 'hits_allowed', 'balks', 'passed_balls', 'home_runs', 'hits', 'rbi', 'runs_scored', 'stolen_bases', 'doubles', 'triples', 'walks_drawn', 'pitcher_strikeouts', 'pitcher_wins', 'pitcher_losses', 'saves', 'holds', 'outfield_assists', 'lob', 'hit_by_pitch', 'at_bats', 'innings_pitched'];

    const fetchPeriod = async () => {
      setPeriodLoading(true);
      const { data: gameStats } = await supabase
        .from('player_game_stats')
        .select('player_id, game_date, is_pitcher, ' + ALL_STAT_KEYS.join(', '))
        .in('player_id', playerIds)
        .gte('game_date', dateStart)
        .lte('game_date', dateEnd);

      const agg: Record<string, Record<string, number>> = {};
      const gamesCount: Record<string, number> = {};
      for (const gs of (gameStats || [])) {
        if (!agg[gs.player_id]) { agg[gs.player_id] = {}; gamesCount[gs.player_id] = 0; }
        gamesCount[gs.player_id]++;
        for (const key of ALL_STAT_KEYS) {
          agg[gs.player_id][key] = (agg[gs.player_id][key] || 0) + Number((gs as any)[key] || 0);
        }
        agg[gs.player_id].is_pitcher = (gs as any).is_pitcher ? 1 : 0;
      }

      const ssMap = new Map<string, SeasonStats>();
      for (const [pid, stats] of Object.entries(agg)) {
        const row = { ...stats, games_played: gamesCount[pid] } as any;
        row.is_pitcher = !!stats.is_pitcher;
        ssMap.set(pid, row as SeasonStats);
      }
      setPeriodStatsMap(ssMap);
      setPeriodLoading(false);
    };
    fetchPeriod();
  }, [timePeriod, addDrop.roster, today]);

  // Active stats map — for stat table columns (raw stats display)
  const activeStatsMap = timePeriod === 'season' ? seasonStatsMap : periodStatsMap;

  // Projected per-game TP from season stats + scoring weights
  const projectedStats = useMemo(() => {
    const m = new Map<string, number>();
    for (const [pid, stats] of seasonStatsMap) {
      const gp = stats.games_played || 1;
      const total = calculateTrashPoints(stats as Record<string, number>, scoringWeights);
      m.set(pid, total / gp);
    }
    return m;
  }, [seasonStatsMap, scoringWeights]);

  // Fetch weekly breakdown when selecting a past week
  const fetchWeeklyStats = useCallback(async (week: number) => {
    if (!addDrop.myTeam || !activeLeague) return;
    setWeeklyLoading(true);

    const { data: matchup } = await supabase
      .from('matchups')
      .select('id, week, home_team_id, away_team_id, home_team_name, away_team_name, home_score, away_score, status, week_start, week_end')
      .eq('league_id', activeLeague.id)
      .eq('week', week)
      .or(`home_team_id.eq.${addDrop.myTeam.id},away_team_id.eq.${addDrop.myTeam.id}`)
      .maybeSingle();

    if (!matchup) { setWeeklyLoading(false); return; }
    setWeeklyMatchup(matchup);

    // SAB-752: these used to run in parallel, with player_match_scores fetched for
    // the WHOLE LEAGUE-WEEK (`league_id` + `matchup_week`, no team filter) and
    // then narrowed to this team on the client. Two problems with that grain:
    //
    //  1. WRONG GRAIN. player_match_scores carries a `team_id`. Filtering only by
    //     league+week matches rows belonging to OTHER teams, so a player rostered
    //     on two teams in the same league-week is counted twice. 97 such duplicate
    //     groups exist on prod today (e.g. Nick Allen, league 3bc09205, week 1,
    //     two teams, identical rows).
    //
    //  2. UNBOUNDED. Up to 1,287 rows for one league-week on prod against
    //     PostgREST's 1,000-row cap, so rows were silently dropped before the
    //     client-side filter ever saw them.
    //
    // Filtering by team_id fixes (1) directly and bounds the read as a
    // consequence rather than as the goal: worst case drops from 1,287 per
    // league-week to 521 per TEAM-week (avg 66). Note it is NOT one row per
    // player — the grain is one row per player per GAME, so a 25-player roster
    // returns ~150 rows for a single week.
    //
    // Still paged: 521 is 52% of the cap, and a bound that is already half
    // consumed is the exact shape SAB-752 is about.
    //
    // SCOPE OF THE BUG THIS FIXES — deliberately narrow. The headline matchup
    // score on this screen (~line 1607) reads weeklyMatchup.home_score/away_score
    // straight from the `matchups` table: engine-computed and ALWAYS CORRECT.
    // Truncation only ever affected the SECONDARY box-score subtotal
    // (MatchupBoxScore.tsx:137) and the per-player rows. Measured on prod: that
    // subtotal rendered 29.0 where the true figure is 756.0, while the headline
    // showed 756.0 throughout. That is why 44 paying users across a full season
    // never reported a wrong score — the number they read was never wrong.
    //
    // KNOWN REMAINING LIMITATION (not fixed here): the player list and the
    // is_starter flags still come from the CURRENT roster_assignments, not from
    // the week being viewed, so a historical week whose roster has since changed
    // still renders against today's roster.
    const rosterRes = await supabase.from('roster_assignments')
      .select('player_id, is_starter, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
      .eq('team_id', addDrop.myTeam.id);

    if (rosterRes.error) {
      console.error('[MLBBetaMyTeam] roster load failed:', rosterRes.error.message);
      setWeeklyLoading(false);
      return;
    }

    const rosterPlayerIds = (rosterRes.data || []).map((r: any) => r.player_id);

    // SAB-752: paged, and error-checked on every page. An unchecked read that
    // returns an empty set is what makes this class invisible — every player
    // would render 0 and look like a scoreless week rather than a failed query.
    const scoreRowsAll: { player_id: string; total_points: number | string }[] = [];
    const PAGE = 1000;
    let from = 0;
    let pageCount = 0;
    let loadFailed = false;
    while (true) {
      const { data: page, error: pageErr } = await supabase
        .from('player_match_scores')
        .select('player_id, trash_points, anti_points, total_points')
        .eq('league_id', activeLeague.id)
        .eq('matchup_week', week)
        .eq('team_id', addDrop.myTeam.id)
        .range(from, from + PAGE - 1);
      if (pageErr) {
        console.error('[MLBBetaMyTeam] weekly score load failed:', pageErr.message);
        loadFailed = true;
        break;
      }
      pageCount++;
      if (!page || page.length === 0) break;
      scoreRowsAll.push(...(page as any));
      if (page.length < PAGE) break;
      from += PAGE;
    }

    if (loadFailed) {
      // Fail visibly rather than rendering a confidently wrong 0 for everyone.
      setWeeklyLoading(false);
      return;
    }

    // Aggregate scores per player. The server-side .in() already restricted this
    // to the roster, so no client-side filter is needed.
    const tpByPlayer = new Map<string, number>();
    for (const s of scoreRowsAll) {
      tpByPlayer.set(s.player_id, (tpByPlayer.get(s.player_id) || 0) + Number(s.total_points));
    }

    // SAB-752: assertable counters — a resolution rate rather than the absence of
    // an error. `scored` below the roster size is normal (bench players with no
    // game); `scoreRows` materially exceeding the roster size would mean the
    // server-side filter is not doing its job.
    const scored = tpByPlayer.size;
    const weeklyTotal = [...tpByPlayer.values()].reduce((a, b) => a + b, 0);
    console.log(
      `[MLBBetaMyTeam] week ${week}: roster=${rosterPlayerIds.length} (current) ` +
      `scoreRows=${scoreRowsAll.length} pages=${pageCount} playersWithScore=${scored} ` +
      `weeklyTotal=${weeklyTotal.toFixed(1)}`
    );

    const players: BoxScorePlayer[] = (rosterRes.data || []).map((r: any) => ({
      player_id: r.player_id,
      player_name: r.players?.full_name || 'Unknown',
      position: r.players?.position_abbreviation || '??',
      team_name: r.players?.mlb_team_abbreviation || '',
      headshot_url: r.players?.headshot_url || null,
      is_starter: r.is_starter,
      stats: {},
      total_points: tpByPlayer.get(r.player_id) || 0,
    }));

    setWeeklyPlayers(players);
    setWeeklyLoading(false);
  }, [addDrop.myTeam, activeLeague]);

  useEffect(() => {
    if (typeof weeklyView === 'number') {
      fetchWeeklyStats(weeklyView);
    }
  }, [weeklyView, fetchWeeklyStats]);

  const handleSaveTeamName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed) {
      toast({ title: 'Team name cannot be empty', variant: 'destructive' });
      return;
    }
    if (trimmed.length > 24) {
      toast({ title: 'Team name must be 24 characters or less', variant: 'destructive' });
      return;
    }
    setIsSavingName(true);
    const success = await renameMyTeam(trimmed);
    setIsSavingName(false);
    if (success) {
      setIsEditingName(false);
      toast({ title: 'Team name updated!' });
    } else {
      toast({ title: 'Failed to update team name', variant: 'destructive' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
  };

  const { roster, rosterLoading, myTeam, rosterLocked, lockMessage, faabRemaining, moveToIL: rawMoveToIL, activateFromIL: rawActivateFromIL, isILEligible, lineupLockType, isPlayerLocked, getPlayerLockReason, refreshPerGameLocks } = addDrop;

  // Refresh daily overlay after IL operations so effectiveRoster picks up the change
  const refreshDailyOverlay = useCallback(async () => {
    if (!isDailyMode || !myTeam) return;
    const { data: dailyRows } = await supabase
      .from('daily_lineups')
      .select('player_id, slot, is_starter, locked_at')
      .eq('team_id', myTeam.id)
      .eq('game_date', viewDate);
    if (dailyRows && dailyRows.length > 0) {
      const overlay = new Map<string, { slot: string; is_starter: boolean }>();
      dailyRows.forEach((r: any) => overlay.set(r.player_id, { slot: r.slot, is_starter: r.is_starter }));
      setDailyOverlay(overlay);
      const locks = new Map<string, string>();
      dailyRows.filter((r: any) => r.locked_at).forEach((r: any) => locks.set(r.player_id, r.locked_at));
      setDailyLocks(locks);
    }
  }, [isDailyMode, myTeam, viewDate]);

  const moveToIL = useCallback(async (player: any) => {
    const ok = await rawMoveToIL(player);
    if (ok && isDailyMode) await refreshDailyOverlay();
    return ok;
  }, [rawMoveToIL, isDailyMode, refreshDailyOverlay]);

  const activateFromIL = useCallback(async (player: any) => {
    const ok = await rawActivateFromIL(player);
    if (ok && isDailyMode) await refreshDailyOverlay();
    return ok;
  }, [rawActivateFromIL, isDailyMode, refreshDailyOverlay]);

  // Pending incoming trades notification
  const [pendingTradeCount, setPendingTradeCount] = useState(0);
  const [activeTab, setActiveTab] = useState('roster');

  useEffect(() => {
    if (!myTeam?.id || !activeLeague?.id) return;
    const fetchPendingTrades = async () => {
      const { count } = await supabase
        .from('trades')
        .select('id', { count: 'exact', head: true })
        .eq('league_id', activeLeague.id)
        .eq('receiver_team_id', myTeam.id)
        .in('status', ['pending', 'countered']);
      setPendingTradeCount(count || 0);
    };
    fetchPendingTrades();

    const channel = supabase
      .channel(`pending-trades-${myTeam.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trades', filter: `receiver_team_id=eq.${myTeam.id}` }, () => { fetchPendingTrades(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [myTeam?.id, activeLeague?.id]);

  // Swap two players' slots
  const handleSwap = async (target: RosterPlayer) => {
    if (swapProcessing) return;
    if (!swapSource || !myTeam || swapSource.assignment_id === target.assignment_id) {
      setSwapSource(null);
      return;
    }

    // Enforce lineup locks
    if (rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage, variant: 'destructive' });
      setSwapSource(null);
      return;
    }
    if (isPlayerLocked(swapSource.player_id)) {
      toast({ title: 'Player locked', description: `${swapSource.full_name}: ${getPlayerLockReason(swapSource.player_id)}`, variant: 'destructive' });
      setSwapSource(null);
      return;
    }
    if (isPlayerLocked(target.player_id)) {
      toast({ title: 'Player locked', description: `${target.full_name}: ${getPlayerLockReason(target.player_id)}`, variant: 'destructive' });
      setSwapSource(null);
      return;
    }

    // Enforce position eligibility — both players must be eligible for each other's slot
    if (!isEligibleForSlotD(swapSource.position_abbreviation, target.slot, swapSource.position_eligibility) ||
        !isEligibleForSlotD(target.position_abbreviation, swapSource.slot, target.position_eligibility)) {
      toast({
        title: 'Invalid swap',
        description: `${swapSource.full_name} (${swapSource.position_abbreviation}) is not eligible for the ${target.slot} slot, or ${target.full_name} (${target.position_abbreviation}) is not eligible for the ${swapSource.slot} slot.`,
        variant: 'destructive',
      });
      setSwapSource(null);
      return;
    }

    // Fresh per-game lock check right before executing (closes UI race window)
    if (isDailyMode && lineupLockType === 'per_game') {
      const freshLocks = await refreshPerGameLocks();
      if (freshLocks.has(swapSource.player_id)) {
        toast({ title: 'Player locked', description: `${swapSource.full_name}'s game has already started. Lineup is locked.`, variant: 'destructive' });
        setSwapSource(null);
        return;
      }
      if (freshLocks.has(target.player_id)) {
        toast({ title: 'Player locked', description: `${target.full_name}'s game has already started. Lineup is locked.`, variant: 'destructive' });
        setSwapSource(null);
        return;
      }
    }

    setSwapProcessing(true);

    if (isDailyMode) {
      // Per-game mode: swap in daily_lineups for viewDate
      const { body: swapBody, error: swapErr } = await invokeDailyLineup(
        { action: 'swap', team_id: myTeam.id, game_date: viewDate, player_id: swapSource.player_id, target_player_id: target.player_id },
      );
      if (swapErr) {
        console.error('Swap failed:', swapErr);
        toast({ title: 'Swap failed', description: swapErr, variant: 'destructive' });
      } else {
        const dateLabel = isViewingToday ? 'today' : formatViewDate(viewDate);
        toast({ title: 'Saved ✓', description: `Swapped ${swapSource.full_name} ↔ ${target.full_name} (${dateLabel})` });
        // Sync roster_assignments sequentially via BENCH to avoid unique constraint collision.
        // BENCH is excluded from the unique index so it acts as a safe intermediate slot.
        await supabase.from('roster_assignments').update({ slot: 'BENCH', is_starter: false }).eq('id', swapSource.assignment_id);
        await supabase.from('roster_assignments').update({ slot: swapSource.slot, is_starter: swapSource.is_starter }).eq('id', target.assignment_id);
        await supabase.from('roster_assignments').update({ slot: target.slot, is_starter: target.is_starter }).eq('id', swapSource.assignment_id);
        // Log lineup changes
        if (activeLeague?.id) {
          const txns: any[] = [];
          if (swapSource.is_starter !== target.is_starter) {
            txns.push({ league_id: activeLeague.id, team_id: myTeam.id, player_id: swapSource.player_id, transaction_type: 'lineup_change', slot: target.slot, meta: { old_is_starter: swapSource.is_starter, new_is_starter: target.is_starter, old_slot: swapSource.slot, trigger: 'swap_daily' } });
            txns.push({ league_id: activeLeague.id, team_id: myTeam.id, player_id: target.player_id, transaction_type: 'lineup_change', slot: swapSource.slot, meta: { old_is_starter: target.is_starter, new_is_starter: swapSource.is_starter, old_slot: target.slot, trigger: 'swap_daily' } });
          }
          if (txns.length > 0) supabase.from('roster_transactions').insert(txns).then(() => {});
        }
        // Re-fetch daily overlay
        const { data: dailyRows } = await supabase
          .from('daily_lineups')
          .select('player_id, slot, is_starter, locked_at')
          .eq('team_id', myTeam.id)
          .eq('game_date', viewDate);
        if (dailyRows) {
          const overlay = new Map<string, { slot: string; is_starter: boolean }>();
          dailyRows.forEach((r: any) => overlay.set(r.player_id, { slot: r.slot, is_starter: r.is_starter }));
          setDailyOverlay(overlay);
        }
        await addDrop.fetchRoster();
      }
    } else {
      // Weekly mode: atomic delete+insert swap to avoid unique constraint violations and ensure rollback safety
      const { error: delErr } = await supabase.from('roster_assignments').delete().in('id', [swapSource.assignment_id, target.assignment_id]);
      if (delErr) {
        toast({ title: 'Swap failed', description: delErr.message, variant: 'destructive' });
        setSwapSource(null); setSwapProcessing(false); return;
      }
      const { error: insErr } = await supabase.from('roster_assignments').insert([
        { team_id: myTeam.id, player_id: swapSource.player_id, slot: target.slot, is_starter: target.is_starter },
        { team_id: myTeam.id, player_id: target.player_id, slot: swapSource.slot, is_starter: swapSource.is_starter },
      ]);
      if (insErr) {
        // Recovery: re-insert original assignments
        await supabase.from('roster_assignments').insert([
          { team_id: myTeam.id, player_id: swapSource.player_id, slot: swapSource.slot, is_starter: swapSource.is_starter },
          { team_id: myTeam.id, player_id: target.player_id, slot: target.slot, is_starter: target.is_starter },
        ]);
        toast({ title: 'Swap failed', description: insErr.message, variant: 'destructive' });
      }
      if (delErr || insErr) {
        toast({ title: 'Swap failed', description: (delErr || insErr)?.message, variant: 'destructive' });
      } else {
        toast({ title: 'Saved ✓', description: `Swapped ${swapSource.full_name} ↔ ${target.full_name}` });
        // Log lineup changes
        if (activeLeague?.id && swapSource.is_starter !== target.is_starter) {
          supabase.from('roster_transactions').insert([
            { league_id: activeLeague.id, team_id: myTeam.id, player_id: swapSource.player_id, transaction_type: 'lineup_change', slot: target.slot, meta: { old_is_starter: swapSource.is_starter, new_is_starter: target.is_starter, old_slot: swapSource.slot, trigger: 'swap_weekly' } },
            { league_id: activeLeague.id, team_id: myTeam.id, player_id: target.player_id, transaction_type: 'lineup_change', slot: swapSource.slot, meta: { old_is_starter: target.is_starter, new_is_starter: swapSource.is_starter, old_slot: target.slot, trigger: 'swap_weekly' } },
          ]).then(() => {});
        }
        await addDrop.fetchRoster();
      }
    }
    setSwapSource(null);
    setSwapProcessing(false);
  };

  // Move swap source to an empty starter slot (no swap partner needed)
  const handleMoveToEmptySlot = async (targetSlot: string) => {
    if (swapProcessing) return;
    if (!swapSource || !myTeam) { setSwapSource(null); return; }

    // Guard: verify slot is truly empty (prevents duplicates from stale UI state)
    const slotOccupied = [...starters, ...ilPlayers].some(p => p.slot === targetSlot && p.assignment_id !== swapSource.assignment_id);
    if (slotOccupied) {
      toast({ title: 'Slot occupied', description: `The ${targetSlot} slot is no longer empty. Please refresh.`, variant: 'destructive' });
      setSwapSource(null);
      await addDrop.fetchRoster();
      return;
    }

    if (rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage, variant: 'destructive' });
      setSwapSource(null);
      return;
    }
    if (isPlayerLocked(swapSource.player_id)) {
      toast({ title: 'Player locked', description: `${swapSource.full_name}: ${getPlayerLockReason(swapSource.player_id)}`, variant: 'destructive' });
      setSwapSource(null);
      return;
    }

    if (!isEligibleForSlotD(swapSource.position_abbreviation, targetSlot, swapSource.position_eligibility)) {
      toast({ title: 'Invalid move', description: `${swapSource.full_name} (${swapSource.position_abbreviation}) is not eligible for the ${targetSlot} slot.`, variant: 'destructive' });
      setSwapSource(null);
      return;
    }

    // Fresh per-game lock check right before executing
    if (isDailyMode && lineupLockType === 'per_game') {
      const freshLocks = await refreshPerGameLocks();
      if (freshLocks.has(swapSource.player_id)) {
        toast({ title: 'Player locked', description: `${swapSource.full_name}'s game has already started. Lineup is locked.`, variant: 'destructive' });
        setSwapSource(null);
        return;
      }
    }

    setSwapProcessing(true);

    if (isDailyMode) {
      const { body: moveBody, error: moveErr } = await invokeDailyLineup(
        { action: 'swap', team_id: myTeam.id, game_date: viewDate, player_id: swapSource.player_id, target_slot: targetSlot },
      );
      if (moveErr) {
        console.error('Move failed:', moveErr);
        toast({ title: 'Move failed', description: moveErr, variant: 'destructive' });
      } else {
        const dateLabel = isViewingToday ? 'today' : formatViewDate(viewDate);
        toast({ title: 'Saved ✓', description: `Moved ${swapSource.full_name} to ${targetSlot} (${dateLabel})` });
        // Sync roster_assignments — clear target slot first to avoid unique constraint
        const multiSlots = ['BENCH', 'IL', 'OF', 'SP'];
        if (!multiSlots.includes(targetSlot)) {
          await supabase.from('roster_assignments').update({ slot: 'BENCH', is_starter: false })
            .eq('team_id', myTeam.id).eq('slot', targetSlot).neq('id', swapSource.assignment_id);
        }
        await supabase.from('roster_assignments').update({ slot: targetSlot, is_starter: true }).eq('id', swapSource.assignment_id);
        // Log lineup change if is_starter changed
        if (activeLeague?.id && !swapSource.is_starter) {
          supabase.from('roster_transactions').insert({
            league_id: activeLeague.id, team_id: myTeam.id, player_id: swapSource.player_id,
            transaction_type: 'lineup_change', slot: targetSlot,
            meta: { old_is_starter: false, new_is_starter: true, old_slot: swapSource.slot, trigger: 'move_to_slot_daily' },
          }).then(() => {});
        }
        // Re-fetch daily overlay
        const { data: dailyRows } = await supabase
          .from('daily_lineups')
          .select('player_id, slot, is_starter, locked_at')
          .eq('team_id', myTeam.id)
          .eq('game_date', viewDate);
        if (dailyRows) {
          const overlay = new Map<string, { slot: string; is_starter: boolean }>();
          dailyRows.forEach((r: any) => overlay.set(r.player_id, { slot: r.slot, is_starter: r.is_starter }));
          setDailyOverlay(overlay);
        }
        await addDrop.fetchRoster();
      }
    } else {
      // Weekly mode — atomic move: clears target slot + moves player + syncs daily_lineups
      const { error } = await supabase.rpc('move_to_slot_atomic', {
        p_team_id: myTeam.id,
        p_player_id: swapSource.player_id,
        p_from_slot: swapSource.slot,
        p_to_slot: targetSlot,
      });
      if (error) {
        toast({ title: 'Move failed', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Saved ✓', description: `Moved ${swapSource.full_name} to ${targetSlot}` });
        // Log lineup change if is_starter changed
        if (activeLeague?.id && !swapSource.is_starter) {
          supabase.from('roster_transactions').insert({
            league_id: activeLeague.id, team_id: myTeam.id, player_id: swapSource.player_id,
            transaction_type: 'lineup_change', slot: targetSlot,
            meta: { old_is_starter: false, new_is_starter: true, old_slot: swapSource.slot, trigger: 'move_to_slot_weekly' },
          }).then(() => {});
        }
        await addDrop.fetchRoster();
      }
    }
    setSwapSource(null);
    setSwapProcessing(false);
  };

  // One-way bench: move a starter to BENCH, leaving the slot empty.
  // No partner required, no eligibility check (BENCH accepts any position).
  const handleBench = async (player: RosterPlayer) => {
    if (swapProcessing) return;
    if (!myTeam) return;
    if (player.slot === 'BENCH' || player.slot === 'IL') {
      setSwapSource(null);
      return;
    }

    if (rosterLocked) {
      toast({ title: 'Roster locked', description: lockMessage, variant: 'destructive' });
      setSwapSource(null);
      return;
    }
    if (isPlayerLocked(player.player_id)) {
      toast({ title: 'Player locked', description: `${player.full_name}: ${getPlayerLockReason(player.player_id)}`, variant: 'destructive' });
      setSwapSource(null);
      return;
    }

    // Fresh per-game lock check right before executing (closes UI race window)
    if (isDailyMode && lineupLockType === 'per_game') {
      const freshLocks = await refreshPerGameLocks();
      if (freshLocks.has(player.player_id)) {
        toast({ title: 'Player locked', description: `${player.full_name}'s game has already started. Lineup is locked.`, variant: 'destructive' });
        setSwapSource(null);
        return;
      }
    }

    const vacatedSlot = player.slot;
    setSwapProcessing(true);

    if (isDailyMode) {
      const { error: benchErr } = await invokeDailyLineup(
        { action: 'swap', team_id: myTeam.id, game_date: viewDate, player_id: player.player_id, target_slot: 'BENCH' },
      );
      if (benchErr) {
        console.error('Bench failed:', benchErr);
        toast({ title: 'Bench failed', description: benchErr, variant: 'destructive' });
      } else {
        const dateLabel = isViewingToday ? 'today' : formatViewDate(viewDate);
        toast({ title: 'Saved ✓', description: `Moved ${player.full_name} to bench (${dateLabel})` });
        // Sync roster_assignments (base lineup) — BENCH is excluded from unique index, no collision risk
        await supabase.from('roster_assignments').update({ slot: 'BENCH', is_starter: false }).eq('id', player.assignment_id);
        if (activeLeague?.id) {
          supabase.from('roster_transactions').insert({
            league_id: activeLeague.id, team_id: myTeam.id, player_id: player.player_id,
            transaction_type: 'lineup_change', slot: 'BENCH',
            meta: { old_is_starter: true, new_is_starter: false, old_slot: vacatedSlot, trigger: 'bench_daily' },
          }).then(() => {});
        }
        // Re-fetch daily overlay
        const { data: dailyRows } = await supabase
          .from('daily_lineups')
          .select('player_id, slot, is_starter, locked_at')
          .eq('team_id', myTeam.id)
          .eq('game_date', viewDate);
        if (dailyRows) {
          const overlay = new Map<string, { slot: string; is_starter: boolean }>();
          dailyRows.forEach((r: any) => overlay.set(r.player_id, { slot: r.slot, is_starter: r.is_starter }));
          setDailyOverlay(overlay);
        }
        await addDrop.fetchRoster();
      }
    } else {
      // Weekly mode — plain UPDATE; BENCH is excluded from unique_team_starter_slot
      const { error } = await supabase.from('roster_assignments')
        .update({ slot: 'BENCH', is_starter: false })
        .eq('id', player.assignment_id);
      if (error) {
        toast({ title: 'Bench failed', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Saved ✓', description: `Moved ${player.full_name} to bench` });
        if (activeLeague?.id) {
          supabase.from('roster_transactions').insert({
            league_id: activeLeague.id, team_id: myTeam.id, player_id: player.player_id,
            transaction_type: 'lineup_change', slot: 'BENCH',
            meta: { old_is_starter: true, new_is_starter: false, old_slot: vacatedSlot, trigger: 'bench_weekly' },
          }).then(() => {});
        }
        await addDrop.fetchRoster();
      }
    }

    setSwapSource(null);
    setSwapProcessing(false);
  };

  // Search free agents for drop-first flow
  const searchFreeAgents = async (query: string) => {
    if (!activeLeague || !myTeam) return;
    setFaLoading(true);

    const { data: allRostered } = await supabase
      .from('roster_assignments')
      .select('player_id')
      .in('team_id', teams.map(t => t.id));

    const rosteredIds = new Set((allRostered || []).map(r => r.player_id));

    let playerQuery = supabase
      .from('players')
      .select('id, full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, headshot_url, status')
      .eq('sport', 'mlb')
      .in('status', ['Active', 'Injured 10-Day', 'Injured 15-Day', 'Injured 60-Day', 'Injured 7-Day', 'Injured - Full Season', 'Day-To-Day'])
      .order('full_name')
      .limit(50);

    if (query.trim()) {
      playerQuery = playerQuery.ilike('full_name', `%${query.trim()}%`);
    }

    const { data } = await playerQuery;
    const available = (data || []).filter(p => !rosteredIds.has(p.id)).map(p => ({
      ...p,
      player_status: (p as any).status || 'Active',
    }));
    setFreeAgents(available);
    setFaLoading(false);
  };

  const handleDropClick = (player: RosterPlayer) => {
    setDropConfirmTarget(player);
    setDropConfirmOpen(true);
  };

  const handleConfirmDrop = async () => {
    if (!dropConfirmTarget) return;
    await addDrop.executeDrop(dropConfirmTarget);
    setDropConfirmOpen(false);
    setDropConfirmTarget(null);
  };

  const handlePickFA = (fa: FreeAgentPlayer) => {
    setAddTarget(fa);
    setShowFASearch(false);
    setAddDropOpen(true);
  };

  // Apply daily lineup overlay (slot/is_starter from daily_lineups for viewDate)
  // Must be above early returns to satisfy React hooks rules
  const effectiveRoster = useMemo(() => {
    if (!isDailyMode || !dailyOverlay) return roster;
    return roster.map(p => {
      // IL is a roster state, not a daily lineup preference.
      // roster_assignments (source of `roster`) is authoritative for IL status.
      if (p.slot === 'IL') {
        return { ...p, slot: 'IL', is_starter: false };
      }
      const override = dailyOverlay.get(p.player_id);
      if (!override) return p;
      // Don't let a stale daily overlay put someone on IL if roster_assignments doesn't
      if (override.slot === 'IL' && p.slot !== 'IL') {
        return p;
      }
      return { ...p, slot: override.slot, is_starter: override.is_starter };
    });
  }, [roster, isDailyMode, dailyOverlay]);

  if (rosterLoading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
      </div>
    );
  }

  if (!myTeam) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">No team found</p>
          <p className="text-sm mt-1">You don't have a team in this league yet.</p>
        </CardContent>
      </Card>
    );
  }

  const sorted = [...effectiveRoster].sort((a, b) => {
    if (a.is_starter !== b.is_starter) return a.is_starter ? -1 : 1;
    const ai = STARTER_SLOTS.indexOf(normalizeSlot(a.slot) as (typeof STARTER_SLOTS)[number]);
    const bi = STARTER_SLOTS.indexOf(normalizeSlot(b.slot) as (typeof STARTER_SLOTS)[number]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  // Players whose slot isn't in STARTER_SLOTS (e.g. DH when DH min=0) must fall into bench
  // even if is_starter=true, otherwise they silently disappear from the UI.
  const starterSlotSet = new Set(STARTER_SLOTS);
  const starters = sorted.filter(p => p.is_starter && starterSlotSet.has(normalizeSlot(p.slot)));
  const bench = sorted.filter(p => (!p.is_starter || !starterSlotSet.has(normalizeSlot(p.slot))) && normalizeSlot(p.slot) !== 'IL');
  const ilPlayers = sorted.filter(p => normalizeSlot(p.slot) === 'IL');

  // In daily mode, past dates are read-only
  const dailyReadOnly = isDailyMode && isViewingPast;

  // ─── Lineup Validation ─────────────────────────────────────
  // Detect empty starter slots that could be filled by bench players
  const normalizePos = (pos: string) => {
    if (['LF', 'CF', 'RF'].includes(pos)) return 'OF';
    return pos;
  };

  const lineupIssues = (() => {
    const issues: { slot: string; benchCandidates: RosterPlayer[] }[] = [];
    const usedCandidateIds = new Set<string>();
    const reportedBaseSlots = new Set<string>();

    const filledSlots = new Set(starters.map(s => s.slot));
    const emptySlots = STARTER_SLOTS.filter(s => !filledSlots.has(s));

    // Group empty slots by base position to deduplicate (e.g. OF1, OF2, OF3 → one "OF" issue)
    const emptyByBase = new Map<string, string[]>();
    for (const slot of emptySlots) {
      const base = slot.replace(/\d+$/, '');
      if (!emptyByBase.has(base)) emptyByBase.set(base, []);
      emptyByBase.get(base)!.push(slot);
    }

    for (const [baseSlot, slots] of emptyByBase) {
      const candidates = bench.filter(p => {
        if (usedCandidateIds.has(p.assignment_id)) return false;
        if (p.player_status !== 'Active') return false;
        const normPos = normalizePos(p.position_abbreviation);
        if (baseSlot === 'UTIL' || baseSlot === 'DH') return true;
        if (baseSlot === 'OF') return ['OF', 'LF', 'CF', 'RF'].includes(p.position_abbreviation);
        if (baseSlot === 'P' || baseSlot === 'SP' || baseSlot === 'RP') return ['P', 'SP', 'RP', 'TWP'].includes(p.position_abbreviation);
        return normPos === baseSlot;
      });
      if (candidates.length > 0) {
        const label = slots.length > 1 ? `${baseSlot} (x${slots.length})` : baseSlot;
        issues.push({ slot: label, benchCandidates: candidates });
        candidates.forEach(c => usedCandidateIds.add(c.assignment_id));
        reportedBaseSlots.add(baseSlot);
      }
    }

    const injuredStarters = starters.filter(p => p.player_status !== 'Active');
    for (const p of injuredStarters) {
      const baseSlot = p.slot.replace(/\d+$/, '');
      if (reportedBaseSlots.has(baseSlot)) continue;
      const replacement = bench.find(b => {
        if (usedCandidateIds.has(b.assignment_id)) return false;
        if (b.player_status !== 'Active') return false;
        const normPos = normalizePos(b.position_abbreviation);
        if (baseSlot === 'UTIL' || baseSlot === 'DH') return true;
        if (baseSlot === 'OF') return ['OF', 'LF', 'CF', 'RF'].includes(b.position_abbreviation);
        if (baseSlot === 'SP' || baseSlot === 'RP') return ['P', 'SP', 'RP', 'TWP'].includes(b.position_abbreviation);
        return normPos === baseSlot;
      });
      if (replacement) {
        usedCandidateIds.add(replacement.assignment_id);
        issues.push({ slot: `${p.slot} (${p.full_name}, ${p.player_status})`, benchCandidates: [replacement] });
      }
    }

    return issues;
  })();

  // Auto-optimize: swap best bench players into empty/injured starter slots

  const handleOptimizeLineup = async () => {
    if (!myTeam || rosterLocked) return;
    setOptimizing(true);

    try {
      const filledSlots = new Set(starters.map(s => s.slot));
      const emptySlots = STARTER_SLOTS.filter(s => !filledSlots.has(s));
      const usedBenchIds = new Set<string>();
      const swaps: { assignmentId: string; newSlot: string; isStarter: boolean }[] = [];

      // 1. Fill empty slots with best bench players (skip locked players)
      for (const slot of emptySlots) {
        const baseSlot = slot.replace(/\d+$/, '');
        const candidate = bench
          .filter(p => {
            if (usedBenchIds.has(p.assignment_id)) return false;
            if (p.player_status !== 'Active') return false;
            if (isPlayerLocked(p.player_id)) return false;
            const normPos = normalizePos(p.position_abbreviation);
            if (baseSlot === 'UTIL' || baseSlot === 'DH') return HITTER_POSITIONS.includes(p.position_abbreviation);
            if (baseSlot === 'OF') return ['OF', 'LF', 'CF', 'RF'].includes(p.position_abbreviation);
            if (baseSlot === 'P' || baseSlot === 'SP' || baseSlot === 'RP') return ['P', 'SP', 'RP', 'TWP'].includes(p.position_abbreviation);
            return normPos === baseSlot;
          })
          .sort((a, b) => (projectedStats.get(b.player_id) || 0) - (projectedStats.get(a.player_id) || 0))[0];

        if (candidate) {
          usedBenchIds.add(candidate.assignment_id);
          swaps.push({ assignmentId: candidate.assignment_id, newSlot: slot, isStarter: true });
        }
      }

      // 2. Replace injured starters with healthy bench players (skip locked)
      const injuredStarters = starters.filter(p => p.player_status !== 'Active' && !isPlayerLocked(p.player_id));
      for (const injured of injuredStarters) {
        const baseSlot = injured.slot.replace(/\d+$/, '');
        const replacement = bench
          .filter(p => {
            if (usedBenchIds.has(p.assignment_id)) return false;
            if (p.player_status !== 'Active') return false;
            if (isPlayerLocked(p.player_id)) return false;
            const normPos = normalizePos(p.position_abbreviation);
            if (baseSlot === 'UTIL' || baseSlot === 'DH') return HITTER_POSITIONS.includes(p.position_abbreviation);
            if (baseSlot === 'OF') return ['OF', 'LF', 'CF', 'RF'].includes(p.position_abbreviation);
            if (baseSlot === 'P' || baseSlot === 'SP' || baseSlot === 'RP') return ['P', 'SP', 'RP', 'TWP'].includes(p.position_abbreviation);
            return normPos === baseSlot;
          })
          .sort((a, b) => (projectedStats.get(b.player_id) || 0) - (projectedStats.get(a.player_id) || 0))[0];

        if (replacement) {
          usedBenchIds.add(replacement.assignment_id);
          // Swap: injured -> bench, replacement -> starter slot
          swaps.push({ assignmentId: injured.assignment_id, newSlot: 'BENCH', isStarter: false });
          swaps.push({ assignmentId: replacement.assignment_id, newSlot: injured.slot, isStarter: true });
        }
      }

      if (swaps.length === 0) {
        toast({ title: 'Lineup already optimal', description: 'No changes needed.' });
        setOptimizing(false);
        return;
      }

      // Build a map of current state for audit logging
      const currentStateMap = new Map<string, { player_id: string; slot: string; is_starter: boolean }>();
      for (const p of [...starters, ...bench, ...ilPlayers]) {
        currentStateMap.set(p.assignment_id, { player_id: p.player_id, slot: p.slot, is_starter: p.is_starter });
      }

      // Execute swaps sequentially to avoid unique constraint collisions.
      // Process BENCH-bound moves first (freeing slots), then starter moves.
      const benchFirst = [...swaps].sort((a, b) => (a.isStarter === b.isStarter ? 0 : a.isStarter ? 1 : -1));
      for (const s of benchFirst) {
        await supabase.from('roster_assignments').update({ slot: s.newSlot, is_starter: s.isStarter }).eq('id', s.assignmentId);
      }

      // Sync daily_lineups for today (per_game mode) so the daily overlay matches
      if (isDailyMode) {
        const today = getTodayET();
        for (const s of benchFirst) {
          const prev = currentStateMap.get(s.assignmentId);
          if (!prev) continue;
          await supabase.from('daily_lineups')
            .update({ slot: s.newSlot, is_starter: s.isStarter })
            .eq('team_id', myTeam.id)
            .eq('player_id', prev.player_id)
            .eq('game_date', today);
        }
      }

      // Log lineup changes
      if (activeLeague?.id && myTeam) {
        const txns = swaps
          .filter(s => {
            const prev = currentStateMap.get(s.assignmentId);
            return prev && prev.is_starter !== s.isStarter;
          })
          .map(s => {
            const prev = currentStateMap.get(s.assignmentId)!;
            return {
              league_id: activeLeague.id, team_id: myTeam.id, player_id: prev.player_id,
              transaction_type: 'lineup_change', slot: s.newSlot,
              meta: { old_is_starter: prev.is_starter, new_is_starter: s.isStarter, old_slot: prev.slot, trigger: 'optimize' },
            };
          });
        if (txns.length > 0) supabase.from('roster_transactions').insert(txns).then(() => {});
      }

      toast({ title: 'Saved \u2713', description: `Lineup optimized. ${swaps.length} move${swaps.length !== 1 ? 's' : ''} made.` });
      await addDrop.fetchRoster();
    } catch (err) {
      toast({ title: 'Optimize failed', variant: 'destructive' });
    }
    setOptimizing(false);
  };

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isEditingName ? (
              <>
                <Input
                  ref={nameInputRef}
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value.slice(0, 24))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTeamName();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  maxLength={24}
                  className="h-8 w-48 text-sm font-bold"
                  autoFocus
                  disabled={isSavingName}
                />
                <span className="text-xs text-muted-foreground">{editNameValue.length}/24</span>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleSaveTeamName} disabled={isSavingName}>
                  <Check className="w-3.5 h-3.5 text-primary" />
                </Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancelEdit} disabled={isSavingName}>
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold font-heading text-foreground">{myTeam.name}</h1>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditNameValue(myTeam.name); setIsEditingName(true); }}>
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  disabled={isRefreshing}
                  onClick={async () => {
                    setIsRefreshing(true);
                    await addDrop.fetchRoster();
                    setIsRefreshing(false);
                  }}
                  title="Refresh roster"
                >
                  <RefreshCw className={cn("w-3.5 h-3.5 text-muted-foreground", isRefreshing && "animate-spin")} />
                </Button>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {myTeam.wins}-{myTeam.losses}{myTeam.ties > 0 ? `-${myTeam.ties}` : ''} • {roster.length} players • ${faabRemaining} FAAB
            {projectedStats.size > 0 && (
              <span className="ml-2 text-primary font-medium">
                • PROJ {starters.reduce((s, p) => s + (projectedStats.get(p.player_id) || 0), 0).toFixed(1)}/G
              </span>
            )}
          </p>
        </div>

        {/* Team navigation dropdown — MLB-027: relabeled "Switch team" with Users icon affordance */}
        {onNavigateToTeam && teams.length > 1 && (
          <Select onValueChange={(val) => onNavigateToTeam(val)}>
            <SelectTrigger className="h-9 w-auto min-w-[160px] border-border bg-background text-foreground gap-2">
              <Users className="w-4 h-4 shrink-0 text-muted-foreground" />
              <SelectValue placeholder="Switch team" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border text-popover-foreground">
              {teams
                .filter(t => t.id !== myTeam.id)
                .map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} ({t.wins}-{t.losses})
                  </SelectItem>
                ))
              }
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Pending trade notification */}
      {pendingTradeCount > 0 && activeTab !== 'trades' && (
        <Card className="bg-blue-500/10 border-blue-500/30 cursor-pointer hover:bg-blue-500/15 transition-colors" onClick={() => setActiveTab('trades')}>
          <CardContent className="p-3 flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-blue-400 shrink-0" />
            <span className="text-sm text-blue-400 font-medium">
              You have {pendingTradeCount} pending trade {pendingTradeCount === 1 ? 'proposal' : 'proposals'}
            </span>
            <span className="text-xs text-blue-400/70 ml-auto">View →</span>
          </CardContent>
        </Card>
      )}

      {/* No w-full on Tabs here — w-full + -mx-4 doesn't extend the box right (width: 100%
          stays at parent's inner width; negative margin only allows overlap, not expansion).
          Without w-full, Tabs sizes to its content (TabsList flex w-full + chips flex-1
          resolve to fill the breakout-extended area). Verified at /_chip-test case 3 vs case 4. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="-mx-4 sm:mx-0">
        {/* Mobile — ghost-outline pill matching the Range/View Seg styling below.
            mx-4 cancels the parent Tabs's -mx-4 breakout so the pill aligns with
            the page's padded content (Seg controls in this section all share the
            same horizontal inset). */}
        <div className="sm:hidden mx-4">
          <Seg
            ariaLabel="Roster or trades"
            value={activeTab}
            options={[
              {
                value: 'roster',
                label: (
                  <>
                    <Users className="w-3.5 h-3.5" />
                    <span>Roster</span>
                  </>
                ),
              },
              {
                value: 'trades',
                label: (
                  <>
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    <span>Trades</span>
                    {pendingTradeCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-500 text-[10px] text-white flex items-center justify-center font-bold">
                        {pendingTradeCount}
                      </span>
                    )}
                  </>
                ),
              },
            ]}
            onChange={setActiveTab}
          />
        </div>
        {/* Desktop — existing TabsList unchanged at sm+, hidden on mobile. */}
        <TabsList className="hidden sm:flex w-full p-0 rounded-none [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm">
          <TabsTrigger value="roster" className="flex-1 gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Roster
          </TabsTrigger>
          <TabsTrigger value="trades" className="flex-1 gap-1.5 relative">
            <ArrowLeftRight className="w-3.5 h-3.5" />
            Trades
            {pendingTradeCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-500 text-[10px] text-white flex items-center justify-center font-bold">
                {pendingTradeCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="space-y-4 mt-4">

      {/* Week selector */}
      {availableWeeks.length > 0 && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">
            {weeklyView === 'current' ? 'Season Stats' : `Week ${weeklyView} Recap`}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (weeklyView === 'current') {
                  setWeeklyView(maxWeek);
                } else if (typeof weeklyView === 'number' && weeklyView > 1) {
                  setWeeklyView(weeklyView - 1);
                }
              }}
              disabled={typeof weeklyView === 'number' && weeklyView <= 1}
              className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30"
            >←</button>
            <button
              onClick={() => setWeeklyView('current')}
              className={cn(
                "px-2 py-1 text-[10px] rounded transition-colors",
                weeklyView === 'current' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              Season
            </button>
            <button
              onClick={() => {
                if (weeklyView === 'current') return;
                if (typeof weeklyView === 'number' && weeklyView < maxWeek) {
                  setWeeklyView(weeklyView + 1);
                } else {
                  setWeeklyView('current');
                }
              }}
              disabled={weeklyView === 'current'}
              className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30"
            >→</button>
          </div>
        </div>
      )}

      {/* Weekly box score view */}
      {typeof weeklyView === 'number' && (
        weeklyLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : weeklyMatchup ? (
          <div className="space-y-3">
            {/* Matchup result banner */}
            <Card className="bg-muted/30 border-border overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-muted-foreground">Week {weeklyMatchup.week} • {weeklyMatchup.week_start} – {weeklyMatchup.week_end}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-semibold text-foreground">{weeklyMatchup.away_team_name}</span>
                      <span className={cn("text-lg font-bold font-heading", Number(weeklyMatchup.away_score) > Number(weeklyMatchup.home_score) ? "text-primary" : "text-foreground")}>
                        {Number(weeklyMatchup.away_score).toFixed(1)}
                      </span>
                      <span className="text-xs text-muted-foreground">-</span>
                      <span className={cn("text-lg font-bold font-heading", Number(weeklyMatchup.home_score) > Number(weeklyMatchup.away_score) ? "text-primary" : "text-foreground")}>
                        {Number(weeklyMatchup.home_score).toFixed(1)}
                      </span>
                      <span className="text-sm font-semibold text-foreground">{weeklyMatchup.home_team_name}</span>
                    </div>
                  </div>
                  <Badge className={cn("text-[9px]",
                    weeklyMatchup.status === 'final' ? 'bg-accent text-accent-foreground' :
                    weeklyMatchup.status === 'live' ? 'bg-destructive text-destructive-foreground' :
                    'bg-muted text-muted-foreground'
                  )}>
                    {weeklyMatchup.status.toUpperCase()}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* My team's player box score */}
            <MatchupBoxScore
              players={weeklyPlayers}
              teamName={myTeam.name}
              teamScore={weeklyPlayers.filter(p => p.is_starter).reduce((s, p) => s + p.total_points, 0)}
              isWinner={
                weeklyMatchup.home_team_id === myTeam.id
                  ? Number(weeklyMatchup.home_score) > Number(weeklyMatchup.away_score)
                  : Number(weeklyMatchup.away_score) > Number(weeklyMatchup.home_score)
              }
            />
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center text-muted-foreground text-sm">
              No matchup found for Week {weeklyView}
            </CardContent>
          </Card>
        )
      )}

      {/* Current roster (shown when viewing season stats) */}
      {weeklyView === 'current' && (
        <>

      {rosterLocked && lineupLockType === 'weekly' && (
        <Card className="bg-destructive/10 border-destructive/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-destructive shrink-0" />
            <span className="text-sm text-destructive font-medium">{lockMessage}</span>
          </CardContent>
        </Card>
      )}

      {lineupLockType === 'per_game' && addDrop.playerLocks.size > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-3 flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="text-sm text-amber-500 font-medium">
              Per-game locks active, {addDrop.playerLocks.size} player{addDrop.playerLocks.size !== 1 ? 's' : ''} locked
            </span>
          </CardContent>
        </Card>
      )}

      {swapSource && (
        /* MLB-024 — solid primary fill + white text reads as active interrupt; outlined Cancel reads as action */
        <Card className="bg-primary border-primary">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <span className="text-sm text-primary-foreground font-medium">
              Moving: <strong>{swapSource.full_name}</strong>. Tap a player to swap
            </span>
            <Button
              size="sm"
              variant="outline"
              className="border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground bg-transparent shrink-0"
              onClick={() => setSwapSource(null)}
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Lineup Validation Warning */}
      {!rosterLocked && lineupIssues.length > 0 && (
        <Card className="bg-accent/10 border-accent/30">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-1">
                <AlertTriangle className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-sm font-medium text-accent">Lineup issues detected</span>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {lineupIssues.map((issue, i) => (
                      <li key={i}>
                        • <span className="font-medium text-foreground">{issue.slot}</span> slot, {issue.benchCandidates.length} eligible player{issue.benchCandidates.length !== 1 ? 's' : ''} on bench
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-1.5 text-xs border-accent/30 text-accent hover:bg-accent/10"
                onClick={handleOptimizeLineup}
                disabled={optimizing}
              >
                <Zap className="w-3.5 h-3.5" />
                {optimizing ? 'Optimizing…' : 'Auto-Optimize'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date navigator (per_game daily lineup mode) */}
      {isDailyMode && (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => shiftDate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <button
            onClick={() => setViewDate(today)}
            className={cn(
              "flex items-center gap-1.5 px-3 h-7 rounded-md text-xs font-medium transition-colors",
              isViewingToday ? "bg-primary/15 text-primary" : "bg-muted/50 text-foreground hover:bg-muted"
            )}
          >
            <Calendar className="w-3 h-3" />
            {formatViewDate(viewDate)}
            {isViewingToday && <span className="text-[9px] opacity-70">(Today)</span>}
            {isViewingPast && <span className="text-[9px] text-muted-foreground">(Past)</span>}
          </button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => shiftDate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Past date read-only banner */}
      {isDailyMode && isViewingPast && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 border border-border/50">
          <Lock className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Viewing locked lineup for {formatViewDate(viewDate)}, read only</span>
        </div>
      )}

      {/* MOBILE filter block — Compact Grid (Variation C). Roster badge full-width, then two
          stacked full-width Seg controls (Range, View). Range stacks instead of sharing a 2-up
          grid with View because Range has 5 options here vs Players' 4 — at 358px content
          width, a paired split would compress Range chips and clip "Season". */}
      <div className="sm:hidden space-y-2">
        <span
          className={`block text-xs font-medium px-2 py-1 rounded ${
            addDrop.openActiveSlots <= 0
              ? 'text-amber-400 bg-amber-400/10'
              : 'text-muted-foreground'
          }`}
        >
          Roster: {addDrop.totalRosterSize - addDrop.openActiveSlots}/{addDrop.totalRosterSize}
        </span>
        <Seg
          ariaLabel="Range filter"
          value={(
            { season: 'Season', '7d': '7D', '14d': '14D', '30d': '30D', today: 'Today' } as const
          )[timePeriod]}
          options={['Season', '7D', '14D', '30D', 'Today']}
          onChange={(v) => {
            const map = { Season: 'season', '7D': '7d', '14D': '14d', '30D': '30d', Today: 'today' } as const;
            setTimePeriod(map[v as keyof typeof map] as typeof timePeriod);
          }}
        />
        <Seg
          ariaLabel="View filter"
          value={myTeamStatView === 'pergame' ? 'Per Game' : 'Totals'}
          options={['Totals', 'Per Game']}
          onChange={(v) => setMyTeamStatView(v === 'Per Game' ? 'pergame' : 'season')}
        />
        {periodLoading && timePeriod !== 'season' && (
          <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>
        )}
      </div>

      {/* DESKTOP toolbar — wrapping pills with inline labels. Hidden on mobile (replaced by Seg block above). */}
      {/* Roster count + stat view toggles — MLB-025: separate Range and View segmented controls with labels so they don't bleed into one nine-segment row */}
      <div className="hidden sm:flex sm:flex-row sm:items-end sm:flex-wrap sm:gap-3">
        <span className={`text-xs font-medium px-2 py-1 rounded self-start sm:self-auto ${addDrop.openActiveSlots <= 0 ? 'text-amber-400 bg-amber-400/10' : 'text-muted-foreground'}`}>
          Roster: {addDrop.totalRosterSize - addDrop.openActiveSlots}/{addDrop.totalRosterSize}
        </span>
        {/* Range + View chip groups inherit edge-to-edge layout from the parent Roster/Trades Tabs
            (which has its own -mx-4 breakout). DO NOT add -mx-4 here — that produces a double
            breakout (chips overflow viewport by 18px each side). TabsList strips its default
            p-1 + rounded-md on mobile so chips' borders sit flush. Reverts to inline-flex with
            rounded-md and p-1 on sm+. Range's 6 chips compress to ~65px each at iPhone 14 (390/6). */}
        <div className="flex flex-col gap-0.5 sm:w-auto">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 sm:px-1">Range</span>
          <Tabs value={timePeriod} onValueChange={(v) => setTimePeriod(v as typeof timePeriod)}>
            <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
              <TabsTrigger value="season" className="text-xs px-2 sm:px-3 h-7">Season</TabsTrigger>
              <TabsTrigger value="7d" className="text-xs px-2 sm:px-3 h-7">7D</TabsTrigger>
              <TabsTrigger value="14d" className="text-xs px-2 sm:px-3 h-7">14D</TabsTrigger>
              <TabsTrigger value="30d" className="text-xs px-2 sm:px-3 h-7">30D</TabsTrigger>
              <TabsTrigger value="today" className="text-xs px-2 sm:px-3 h-7">Today</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="flex flex-col gap-0.5 sm:w-auto">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 sm:px-1">View</span>
          <Tabs value={myTeamStatView} onValueChange={(v) => setMyTeamStatView(v as 'season' | 'pergame')}>
            <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
              <TabsTrigger value="season" className="text-xs px-3 h-7">Totals</TabsTrigger>
              <TabsTrigger value="pergame" className="text-xs px-3 h-7">Per Game</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {periodLoading && timePeriod !== 'season' && (
          <span className="text-xs text-muted-foreground animate-pulse pb-1">Loading...</span>
        )}
      </div>

      {/* ═══ HITTING WIDGET (starters + bench combined) ═══ */}
      {(() => {
        const hitterStarterSlots = STARTER_SLOTS.filter(s => !isPitcherSlot(s));
        const hitterStarters = starters.filter(p => hitterStarterSlots.includes(normalizeSlot(p.slot)));
        const hitterBench = bench.filter(p => !isPitcherPlayer(p));
        return (
          <RosterStatsTable
            label={`Hitting (${hitterStarters.length + hitterBench.length})`}
            slots={hitterStarterSlots}
            players={hitterStarters}
            benchPlayers={hitterBench}
            columns={BATTER_COLUMNS}
            statView={myTeamStatView}
            seasonStatsMap={activeStatsMap}
            playerStats={activeTpMap}
            swapSource={swapSource}
            isEligibleForSlotD={isEligibleForSlotD}
            onSwapStart={setSwapSource}
            onSwapTarget={handleSwap}
            onMoveToEmptySlot={handleMoveToEmptySlot}
            onBench={handleBench}
            onDrop={handleDropClick}
            onMoveToIL={moveToIL}
            isILEligible={isILEligible}
            isPlayerLocked={isPlayerLocked}
            getPlayerLockReason={getPlayerLockReason}
            swapProcessing={swapProcessing}
            rosterLocked={rosterLocked || dailyReadOnly}
            normalizeSlot={normalizeSlot}
            onPlayerClick={setPlayerCardId}
            dailyLocks={isDailyMode ? dailyLocks : undefined}
            gameInfoMap={gameInfoMap.size > 0 ? gameInfoMap : undefined}
            probablePitcherMlbIds={probablePitcherMlbIds}
          />
        );
      })()}

      {/* ═══ PITCHING WIDGET (starters + bench combined) ═══ */}
      {(() => {
        const pitcherStarterSlots = STARTER_SLOTS.filter(s => isPitcherSlot(s));
        const pitcherStarters = starters.filter(p => pitcherStarterSlots.includes(normalizeSlot(p.slot)));
        const pitcherBench = bench.filter(p => isPitcherPlayer(p));
        return (
          <RosterStatsTable
            label={`Pitching (${pitcherStarters.length + pitcherBench.length})`}
            slots={pitcherStarterSlots}
            players={pitcherStarters}
            benchPlayers={pitcherBench}
            columns={PITCHER_COLUMNS}
            statView={myTeamStatView}
            seasonStatsMap={activeStatsMap}
            playerStats={activeTpMap}
            swapSource={swapSource}
            isEligibleForSlotD={isEligibleForSlotD}
            onSwapStart={setSwapSource}
            onSwapTarget={handleSwap}
            onMoveToEmptySlot={handleMoveToEmptySlot}
            onBench={handleBench}
            onDrop={handleDropClick}
            onMoveToIL={moveToIL}
            isILEligible={isILEligible}
            isPlayerLocked={isPlayerLocked}
            getPlayerLockReason={getPlayerLockReason}
            swapProcessing={swapProcessing}
            rosterLocked={rosterLocked || dailyReadOnly}
            normalizeSlot={normalizeSlot}
            onPlayerClick={setPlayerCardId}
            dailyLocks={isDailyMode ? dailyLocks : undefined}
            gameInfoMap={gameInfoMap.size > 0 ? gameInfoMap : undefined}
            probablePitcherMlbIds={probablePitcherMlbIds}
          />
        );
      })()}

      {/* ═══ INJURED LIST ═══ */}
      <RosterStatsTable
        label={`Injured List (${ilPlayers.length})`}
        labelClassName="text-destructive"
        headerClassName="bg-destructive/10"
        headerIcon={<HeartPulse className="w-3.5 h-3.5 text-destructive" />}
        players={ilPlayers}
        columns={ALL_COLUMNS}
        statView={myTeamStatView}
        seasonStatsMap={activeStatsMap}
        playerStats={activeTpMap}
        swapSource={swapSource}
        isEligibleForSlotD={isEligibleForSlotD}
        onSwapStart={setSwapSource}
        onSwapTarget={handleSwap}
        onDrop={handleDropClick}
        isIL
        onActivateFromIL={activateFromIL}
        isPlayerLocked={isPlayerLocked}
        getPlayerLockReason={getPlayerLockReason}
        swapProcessing={swapProcessing}
        rosterLocked={rosterLocked || dailyReadOnly}
        normalizeSlot={normalizeSlot}
        onPlayerClick={setPlayerCardId}
        dailyLocks={isDailyMode ? dailyLocks : undefined}
        gameInfoMap={gameInfoMap.size > 0 ? gameInfoMap : undefined}
        probablePitcherMlbIds={probablePitcherMlbIds}
        emptyMessage="No players on IL"
        emptySubMessage="Use the IL button on a player to place them here"
      />

      {/* Dropped Players this week */}
      {droppedPlayers.length > 0 && (
        <RosterStatsTable
          label={`Dropped (${droppedPlayers.length})`}
          labelClassName="text-muted-foreground"
          headerClassName="bg-muted/20"
          headerIcon={<UserMinus className="w-3.5 h-3.5 text-muted-foreground" />}
          players={droppedPlayers}
          columns={ALL_COLUMNS}
          statView={myTeamStatView}
          seasonStatsMap={activeStatsMap}
          playerStats={activeTpMap}
          swapSource={null}
          isEligibleForSlotD={isEligibleForSlotD}
          onSwapStart={() => {}}
          onSwapTarget={() => {}}
          onDrop={() => {}}
          isPlayerLocked={() => false}
          swapProcessing={false}
          rosterLocked={true}
          normalizeSlot={normalizeSlot}
          onPlayerClick={setPlayerCardId}
          readOnly
        />
      )}

      {/* Free Agent Search (drop-first flow) */}
      {showFASearch && dropTarget && (
        <Card className="bg-card border-border overflow-hidden">
          <div className="px-4 py-2 bg-muted/30 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserMinus className="w-3.5 h-3.5 text-destructive" />
              <span className="text-xs font-semibold text-muted-foreground">
                Dropping {dropTarget.full_name}. Pick replacement
              </span>
            </div>
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setShowFASearch(false); setDropTarget(null); }}>
              Cancel
            </Button>
          </div>
          <div className="p-3">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search free agents..."
                value={faSearch}
                onChange={(e) => { setFaSearch(e.target.value); searchFreeAgents(e.target.value); }}
                className="pl-9"
              />
            </div>
            {faLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : freeAgents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No free agents found</p>
            ) : (
              <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                {freeAgents.map(fa => (
                  <button
                    key={fa.id}
                    onClick={() => handlePickFA(fa)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
                  >
                    <MLBPlayerAvatar
                      name={fa.full_name}
                      headshotUrl={fa.headshot_url}
                      position={fa.position_abbreviation}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">{fa.full_name}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[fa.position_abbreviation])}>
                          {formatPositionDisplay(fa.position_abbreviation, fa.position_eligibility)}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{fa.mlb_team_abbreviation || 'FA'}</span>
                        {fa.player_status && fa.player_status !== 'Active' && (
                          <Badge variant="outline" className="text-[8px] px-1 h-3.5 bg-red-500/20 text-red-400 border-red-500/30">
                            {fa.player_status.replace(/^Injured (\d+)-Day$/, 'IL$1').replace(/^Injured - Full Season$/, 'IL-FS')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <UserPlus className="w-4 h-4 text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* FAAB Bid Confirmation Dialog */}
      <AddDropDialog
        open={addDropOpen}
        onOpenChange={(open) => {
          setAddDropOpen(open);
          if (!open) { setAddTarget(null); setDropTarget(null); }
        }}
        addPlayer={addTarget}
        dropPlayer={dropTarget}
        roster={roster}
        faabRemaining={faabRemaining}
        processing={addDrop.processing}
        onConfirm={addDrop.executeAddDrop}
        onAdd={addDrop.executeAdd}
        onAddToIL={addDrop.executeAddToIL}
        onSubmitClaim={(playerId, faabBid, dropPlayerId) => waiverClaims.submitClaim(playerId, faabBid, dropPlayerId)}
        waiverMode={waiverMode}
        isOnWaiverWire={addTarget ? addDrop.isOnWaiverWire(addTarget.id) : false}
        checkPositionLimit={addDrop.checkPositionLimit}
        checkSlotEligibility={addDrop.checkSlotEligibility}
        isAddPlayerILEligible={addTarget ? addDrop.isFreeAgentILEligible(addTarget) : false}
        availableILSlots={addDrop.availableILSlots}
        openActiveSlots={addDrop.openActiveSlots}
        totalRosterSize={addDrop.totalRosterSize}
      />

        </>
      )}

        </TabsContent>

        <TabsContent value="trades" className="mt-4">
          <MLBBetaTrades />
        </TabsContent>
      </Tabs>

      {playerCardId && (
        <MLBPlayerCardModal
          playerId={playerCardId}
          open={!!playerCardId}
          onOpenChange={(open) => { if (!open) setPlayerCardId(null); }}
        />
      )}

      {/* Standalone Drop Confirmation */}
      <AlertDialog open={dropConfirmOpen} onOpenChange={(open) => { setDropConfirmOpen(open); if (!open) setDropConfirmTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Drop {dropConfirmTarget?.full_name}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDrop}
              disabled={addDrop.processing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {addDrop.processing ? 'Dropping...' : 'Drop'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ESPN-style roster section with three-panel table layout
import type { ReactNode } from 'react';
import { bareDateToDate } from '@/lib/etDate';

interface RosterStatsTableProps {
  label: string;
  labelClassName?: string;
  headerClassName?: string;
  headerIcon?: ReactNode;
  /** If provided, renders a row per slot (with empty placeholders) */
  slots?: string[];
  players: RosterPlayer[];
  columns: StatColumn[];
  statView: 'season' | 'pergame';
  seasonStatsMap: Map<string, SeasonStats>;
  playerStats: Map<string, number>;
  swapSource: RosterPlayer | null;
  isEligibleForSlotD: (pos: string, slot: string, elig?: Record<string, number> | null) => boolean;
  onSwapStart: (p: RosterPlayer) => void;
  onSwapTarget: (p: RosterPlayer) => void;
  onMoveToEmptySlot?: (slot: string) => void;
  onBench?: (p: RosterPlayer) => void;
  onDrop: (p: RosterPlayer) => void;
  onMoveToIL?: (p: RosterPlayer) => void;
  isILEligible?: (p: RosterPlayer) => boolean;
  isIL?: boolean;
  onActivateFromIL?: (p: RosterPlayer) => void;
  isPlayerLocked: (playerId: string) => boolean;
  getPlayerLockReason?: (playerId: string) => string | null;
  swapProcessing: boolean;
  rosterLocked: boolean;
  normalizeSlot: (slot: string) => string;
  onPlayerClick?: (playerId: string) => void;
  dailyLocks?: Map<string, string>;
  gameInfoMap?: Map<number, { opponent: string; isHome: boolean; status: string }>;
  probablePitcherMlbIds?: Set<number>;
  emptyMessage?: string;
  emptySubMessage?: string;
  /** Read-only mode: no action buttons (for dropped players section) */
  readOnly?: boolean;
  /** Extra bench players appended after slot-based starters */
  benchPlayers?: RosterPlayer[];
}

const RosterStatsTable = ({
  label, labelClassName, headerClassName, headerIcon, slots, players, columns, statView,
  seasonStatsMap, playerStats, swapSource, isEligibleForSlotD,
  onSwapStart, onSwapTarget, onMoveToEmptySlot, onBench, onDrop, onMoveToIL, isILEligible, isIL, onActivateFromIL,
  isPlayerLocked, getPlayerLockReason, swapProcessing, rosterLocked, normalizeSlot, onPlayerClick, dailyLocks, gameInfoMap,
  probablePitcherMlbIds, emptyMessage, emptySubMessage, readOnly, benchPlayers,
}: RosterStatsTableProps) => {

  // Build ordered entries: for starters we iterate slots, for bench/IL we iterate players
  type RowEntry = { type: 'player'; player: RosterPlayer; slotLabel: string } | { type: 'empty'; slotLabel: string; fullSlot: string };
  const entries: RowEntry[] = [];

  if (slots) {
    // Track which players have already been placed to handle duplicate slot keys.
    // OF1-OF4, P1-P9, etc. are interchangeable within their position group —
    // if two players share the same slot key, the second overflows to the next
    // open slot in the group instead of being silently dropped.
    const placed = new Set<string>();

    for (const slot of slots) {
      const player = players.find(p => normalizeSlot(p.slot) === slot && !placed.has(p.player_id));
      const slotLabel = slot.replace(/\d+$/, '');
      if (player) {
        placed.add(player.player_id);
        entries.push({ type: 'player', player, slotLabel });
      } else {
        entries.push({ type: 'empty', slotLabel, fullSlot: slot });
      }
    }

    // Fill empty entries with unplaced starters whose slot is in the same position group
    // (e.g., a second OF1 player fills an empty OF3 entry, or SP1/SP2 fill P slots in combined mode)
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (entry.type !== 'empty') continue;
      const basePos = entry.slotLabel; // 'OF', 'P', 'SP', 'RP', etc.
      const overflow = players.find(p => {
        if (placed.has(p.player_id)) return false;
        const playerBase = normalizeSlot(p.slot).replace(/\d+$/, '');
        if (playerBase === basePos) return true;
        // In combined pitcher mode, SP/RP-slotted players should fill P slots
        if (basePos === 'P' && (playerBase === 'SP' || playerBase === 'RP')) return true;
        return false;
      });
      if (overflow) {
        placed.add(overflow.player_id);
        entries[i] = { type: 'player', player: overflow, slotLabel: basePos };
      }
    }
    // Append bench players after starter slots (combined widget mode)
    if (benchPlayers && benchPlayers.length > 0) {
      for (const player of benchPlayers) {
        entries.push({ type: 'player', player, slotLabel: 'BN' });
      }
    }
  } else {
    for (const player of players) {
      const slotLabel = isIL ? 'IL' : player.slot === 'BENCH' ? 'BN' : player.slot.replace(/\d+$/, '');
      entries.push({ type: 'player', player, slotLabel });
    }
  }

  const playerEntries = entries.filter((e): e is { type: 'player'; player: RosterPlayer; slotLabel: string } => e.type === 'player');
  const emptyEntries = entries.filter((e): e is { type: 'empty'; slotLabel: string } => e.type === 'empty');

  // Build StatsRows in display order — real players get stats, empty slots get null stats
  const orderedStatsRows: StatsRow[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry.type === 'player') {
      orderedStatsRows.push({
        id: entry.player.assignment_id,
        stats: seasonStatsMap.get(entry.player.player_id) || null,
        sabPts: playerStats.get(entry.player.player_id) || 0,
      });
    } else {
      orderedStatsRows.push({
        id: `empty-${i}`,
        stats: null,
        sabPts: 0,
      });
    }
  }

  if (entries.length === 0 && emptyMessage) {
    return (
      <Card className="bg-card border-border overflow-hidden">
        <div className={cn("px-4 py-2 border-b border-border flex items-center gap-2", headerClassName || "bg-muted/30")}>
          {headerIcon}
          <span className={cn("text-xs font-semibold uppercase tracking-wider", labelClassName || "text-muted-foreground")}>{label}</span>
        </div>
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          {emptySubMessage && <p className="text-xs text-muted-foreground mt-1">{emptySubMessage}</p>}
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border overflow-hidden">
      <div className={cn("px-4 py-2 border-b border-border flex items-center gap-2", headerClassName || "bg-muted/30")}>
        {headerIcon}
        <span className={cn("text-xs font-semibold uppercase tracking-wider", labelClassName || "text-muted-foreground")}>{label}</span>
      </div>
      <div className="rounded-lg overflow-hidden">
        <div className="flex">
          {/* Sticky left: slot, player info, actions */}
          <div className="flex-shrink-0 z-10 bg-card border-r border-border">
            {/* Header */}
            <div className="flex items-center bg-muted/50 text-muted-foreground text-[10px] font-semibold uppercase tracking-wider border-b border-border h-9">
              <div className="w-10 text-center px-1">Slot</div>
              <div className="min-w-[160px] sm:min-w-[200px] flex-1 px-2">Player</div>
              {/* MLB-023 — mobile-only inline SAB PTS column so users see scoring without horizontal scroll */}
              <div className="w-14 text-right px-1 sm:hidden tabular-nums">SAB</div>
              <div className="w-16 text-center px-1"></div>
            </div>
            {/* Rows */}
            {entries.map((entry, idx) => {
              if (entry.type === 'empty') {
                const swapMode = !!swapSource;
                const emptyEligible = swapMode && swapSource ? isEligibleForSlotD(swapSource.position_abbreviation, entry.fullSlot, swapSource.position_eligibility) : false;
                return (
                  <div
                    key={`empty-${idx}`}
                    onClick={swapMode && emptyEligible && onMoveToEmptySlot ? () => onMoveToEmptySlot(entry.fullSlot) : undefined}
                    className={cn(
                      "flex items-center border-b border-border/40 h-11",
                      idx % 2 === 1 && "bg-muted/10",
                      swapMode && emptyEligible && "cursor-pointer hover:bg-primary/5 border-primary/30",
                      swapMode && !emptyEligible && "opacity-30",
                    )}
                  >
                    <div className="w-10 text-center px-1">
                      <Badge variant="outline" className={cn("text-[9px] px-1 py-0 h-4 font-mono border-dashed", swapMode && emptyEligible ? "text-primary border-primary/50" : "text-muted-foreground/50")}>
                        {entry.slotLabel}
                      </Badge>
                    </div>
                    <div className="w-[160px] sm:w-[200px] px-2 flex items-center gap-2">
                      <div className={cn("w-7 h-7 rounded-full border border-dashed flex items-center justify-center shrink-0", swapMode && emptyEligible ? "bg-primary/10 border-primary/50" : "bg-muted/40 border-border/50")}>
                        <UserPlus className={cn("w-3 h-3", swapMode && emptyEligible ? "text-primary" : "text-muted-foreground/30")} />
                      </div>
                      <span className={cn("text-xs italic", swapMode && emptyEligible ? "text-primary" : "text-muted-foreground/50")}>
                        {swapMode && emptyEligible ? 'Tap to move here' : 'Empty'}
                      </span>
                    </div>
                    <div className="w-14 text-right px-1 sm:hidden tabular-nums text-muted-foreground/40">-</div>
                    <div className="w-16" />
                  </div>
                );
              }

              const player = entry.player;
              const locked = isPlayerLocked(player.player_id);
              const dailyLocked = dailyLocks?.has(player.player_id);
              const isSwapSrc = swapSource?.assignment_id === player.assignment_id;
              const swapMode = !!swapSource;
              const swapEligible = swapSource ? isEligibleForSlotD(swapSource.position_abbreviation, player.slot, swapSource.position_eligibility) && isEligibleForSlotD(player.position_abbreviation, swapSource.slot, player.position_eligibility) : true;
              // Format status badge: "Injured 10-Day" → "IL10", "Designated for Assignment" → "DFA", etc.
              const statusLabel = player.player_status && player.player_status !== 'Active'
                ? player.player_status
                    .replace(/^Injured (\d+)-Day$/, 'IL$1')
                    .replace(/^Injured - Full Season$/, 'IL-F')
                    .replace(/^(\d+)-Day Injured List$/, 'IL$1')
                    .replace(/^Designated for Assignment$/, 'DFA')
                    .replace(/^Reassigned to Minors$/, 'MiLB')
                    .replace(/^Suspended.*$/, 'SUSP')
                    .replace(/^Restricted List$/, 'RSTD')
                    .replace(' Injured List', ' IL')
                : null;
              const gameInfo = player.mlb_team_id != null ? gameInfoMap?.get(player.mlb_team_id) : undefined;

              return (
                <div
                  key={player.assignment_id}
                  onClick={swapMode && !isSwapSrc && swapEligible ? () => onSwapTarget(player) : undefined}
                  className={cn(
                    "flex items-center border-b border-border/40 min-h-11",
                    idx % 2 === 1 && "bg-muted/10",
                    isSwapSrc && "bg-primary/10",
                    isIL && !isSwapSrc && "bg-destructive/5",
                    swapMode && !isSwapSrc && swapEligible && "cursor-pointer hover:bg-primary/5",
                    swapMode && !isSwapSrc && !swapEligible && "opacity-30 cursor-not-allowed",
                  )}
                >
                  <div className="w-10 text-center px-1">
                    <Badge variant="outline" className={cn("text-[9px] px-1 py-0 h-4 font-mono", isIL && "border-destructive/50 text-destructive")}>
                      {entry.slotLabel}
                    </Badge>
                  </div>
                  <div className="min-w-[160px] sm:min-w-[200px] flex-1 px-2 flex items-center gap-1.5">
                    <MLBPlayerAvatar name={player.full_name} headshotUrl={player.headshot_url} position={player.position_abbreviation} size="sm" />
                    <div className="min-w-0 flex-1">
                      <span
                        className={cn("text-xs font-medium text-foreground block truncate", onPlayerClick && "cursor-pointer hover:text-primary hover:underline")}
                        onClick={onPlayerClick ? (e) => { e.stopPropagation(); onPlayerClick(player.player_id); } : undefined}
                      >{player.full_name}</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Badge variant="outline" className={cn("text-[8px] px-1 py-0 h-3.5 shrink-0", positionColors[player.position_abbreviation])}>
                          {formatPositionDisplay(player.position_abbreviation, player.position_eligibility)}
                        </Badge>
                        <span className="text-[9px] text-muted-foreground shrink-0">{player.mlb_team_abbreviation || 'FA'}</span>
                        {gameInfo ? (
                          gameInfo.status === 'postponed' ? (
                            <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-yellow-500/60 text-yellow-500 bg-yellow-500/10 font-semibold shrink-0">PPD</Badge>
                          ) : (
                          <span className={cn("text-[8px] shrink-0",
                            gameInfo.status === 'live' || gameInfo.status === 'In Progress' ? "text-green-500 font-semibold" :
                            gameInfo.status === 'Final' || gameInfo.status === 'Game Over' ? "text-muted-foreground/60" :
                            "text-muted-foreground"
                          )}>
                            {gameInfo.status === 'live' || gameInfo.status === 'In Progress' ? 'LIVE' :
                             gameInfo.status === 'Final' || gameInfo.status === 'Game Over' ? 'Final' :
                             `${gameInfo.isHome ? 'vs' : '@'} ${gameInfo.opponent}${gameInfo.startTime ? ' ' + new Date(gameInfo.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : ''}`}
                          </span>
                          )
                        ) : gameInfoMap && gameInfoMap.size > 0 ? (
                          <span className="text-[8px] text-muted-foreground/40">-</span>
                        ) : null}
                        {player.mlb_id && probablePitcherMlbIds?.has(player.mlb_id) && (
                          <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-orange-400/60 text-orange-500 bg-orange-500/10 font-semibold shrink-0">PP</Badge>
                        )}
                        {statusLabel && (
                          <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-destructive/40 text-destructive bg-destructive/10 shrink-0">
                            {statusLabel}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-14 text-right px-1 sm:hidden tabular-nums font-bold text-primary text-sm">
                    {getSabPtsDisplay(playerStats.get(player.player_id) || 0, statView, seasonStatsMap.get(player.player_id)?.games_played ?? 0)}
                  </div>
                  <div className="w-20 flex items-center justify-center gap-0.5 shrink-0">
                    {!readOnly && swapMode && isSwapSrc && player.is_starter && onBench && !rosterLocked && !locked && (
                      <Button
                        size="sm"
                        variant="default"
                        className="h-6 px-2 text-[10px]"
                        onClick={(e) => { e.stopPropagation(); onBench(player); }}
                        disabled={swapProcessing}
                        title="Move to Bench"
                      >
                        Bench
                      </Button>
                    )}
                    {!readOnly && !swapMode && !locked && !rosterLocked && (
                      <>
                        {isIL && onActivateFromIL ? (
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-primary" onClick={(e) => { e.stopPropagation(); onActivateFromIL(player); }} disabled={swapProcessing} title="Activate from IL">
                            <Activity className="w-3 h-3" />
                          </Button>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => { e.stopPropagation(); onSwapStart(player); }} disabled={swapProcessing} title="Move">
                              <ArrowUpDown className="w-3 h-3" />
                            </Button>
                            {onMoveToIL && isILEligible?.(player) && (
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-400" onClick={(e) => { e.stopPropagation(); onMoveToIL(player); }} disabled={swapProcessing} title="Move to IL">
                                <HeartPulse className="w-3 h-3" />
                              </Button>
                            )}
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDrop(player); }} disabled={swapProcessing} title="Drop">
                          <UserMinus className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    {!readOnly && locked && (
                      <span className="flex items-center gap-0.5" title={dailyLocked ? 'Game started, locked for today' : getPlayerLockReason?.(player.player_id) || 'Locked'}>
                        <Lock className="w-3 h-3 text-muted-foreground" />
                        {dailyLocked && <span className="text-[8px] text-muted-foreground">LIVE</span>}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Scrollable stat columns + pinned SAB PTS (via StatsColumns) */}
          <StatsColumns
            rows={orderedStatsRows}
            columns={columns}
            statView={statView}
          />
        </div>
      </div>
    </Card>
  );
};

export default MLBBetaMyTeam;
